import React, { useEffect, useRef, useState } from 'react'
import { SubjectPageSkeleton } from '../components/SubjectPageSkeleton'
import { ToastNotification } from '../components/ToastNotification'
import { PortalButton } from '../components/PortalButton'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, OfflineError } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { auth, IS_FIREBASE_ENABLED } from '../lib/firebase'
import { sendEmailVerification } from 'firebase/auth'
import { getPackMeta, type PackMeta } from '../lib/offlineDb'
import { UpgradePrompt } from '../components/UpgradePrompt'
import { getOffer, openPricing, PAID_TIERS_ENABLED } from '../lib/payments'
import { daysUntilExam, examCountdownLabel, examCountdownSentence, formatExamDay } from '../lib/examDates'
import { FreeSubjectPickerModal } from '../components/FreeSubjectPickerModal'
import { CooldownElapsedPrompt } from '../components/CooldownElapsedPrompt'
import { cooldownPromptDismissalKey } from '../lib/cooldownPromptKey'
import { Grade12PopupGate } from '../components/Grade12PopupGate'
import {
  ProfileCompletionBanner,
  missingProfileFields,
  isProfileBannerDismissed,
} from '../components/ProfileCompletionBanner'
import { OfflinePackCard } from '../components/OfflinePackCard'
import { ActiveContextSwitcher, type SwitcherProgramme } from '../components/ActiveContextSwitcher'
import { SubjectsBanners } from '../components/SubjectsBanners'
import { NotificationsBanner } from '../components/NotificationsBanner'
import { ProgrammeDetailsSheet } from '../components/ProgrammeDetailsSheet'
import { MiniPresetSheet, type MiniPreset } from '../components/MiniPresetSheet'
import { useSessionQuota, canStartMini as canStartMiniFrom, canStartFull as canStartFullFrom } from '../hooks/useSessionQuota'
import { track as trackAnalytics } from '../lib/analytics'
import { readSubjectsCache, writeSubjectsCache, subjectsPayloadEquals, invalidateSubjectsCache, SUBJECTS_AUX_CACHE_KEY } from '../lib/subjectsCache'
import { sortProgrammesByUniversityThenPriority } from '../lib/sortProgrammes'
import { MODE_LABEL } from '../lib/modeLabels'
import { visibleFirstName } from '../lib/visibleFirstName'
import { ensureOfflineReadiness } from '../lib/offlineReadiness'
import { availableModes } from '../lib/availableModes'

// Formats cooldown copy such as "3 dias" or "2 horas".
function formatCooldown(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms) || ms <= 0) return 'em breve'
  const days = Math.round(ms / (1000 * 60 * 60 * 24))
  if (days >= 1) return `${days} dia${days === 1 ? '' : 's'}`
  const hours = Math.round(ms / (1000 * 60 * 60))
  if (hours >= 1) return `${hours} hora${hours === 1 ? '' : 's'}`
  const minutes = Math.max(1, Math.round(ms / (1000 * 60)))
  return `${minutes} min`
}

interface Topic {
  id: string
  code: string
  name: string
  question_count?: number
  // How many times the student has answered a question tagged to this
  // topic. Drives the "fresh first" sort on the subject card so topics
  // with room to explore surface before saturated ones.
  student_attempts_count?: number
}
interface Subject {
  id: string; code: string; name: string; description: string | null
  requires_subscription: boolean; unlocked: boolean; topics: Topic[]
  // Mirror of !unlocked, kept for clarity at call sites.
  locked?: boolean
  required_for_programmes: string[]   // e.g. ["UEM · Eng. Civil", "UEM · Eng. Informática"]
  subject_weight: number              // 0–100 (percentage of admission score, max across programmes)
  exam_variant: string                // "I" | "II" | "III" | "" - primary variant only
  // Used when the same subject appears with different weights/variants.
  subject_breakdown?: Array<{
    programme_id:           string
    programme_name:         string
    university_short_name:  string
    variant:                string
    weight:                 number
  }>
  // "YYYY-MM-DD" for the exam this student sits in this subject, already
  // narrowed server-side to their university group and their variant.
  // Null (and any past date, which the server filters out) means no
  // countdown is drawn anywhere for this subject.
  exam_date?: string | null
  mastery_score: number | null        // 0.0–1.0 average across topics; null = no attempts
  questions_seen?: number             // total answered across this subject's topics
  // Null means there was no meaningful week-over-week signal to show.
  mastery_delta_7d?: number | null
}

interface WeeklyRecap {
  improved: Array<{ subject_code: string; name: string; delta: number }>
  risky:    Array<{ subject_code: string; name: string; delta: number }>
  next_action: { copy: string; subject_code: string; name: string } | null
}

interface SubjectGate {
  kind?: 'free_n12' | 'placeholder_explore'
  limit: number                          // -1 = unlimited (no gate)
  effective_limit?: number
  cooldown_days: number
  picked_codes: string[]
  picked_at: string | null
  next_swap_at: string | null
  can_swap_now: boolean
  needs_initial_pick: boolean
  // Highlights the student's own school subjects in 12ª mode.
  school_subject_codes?: string[]
}

interface ProgrammeRequirement {
  subject_code: string; variant: string; weight: number
  /** Present since the "Disciplina não disponível no Tutor" work. Optional so a cached
   *  response from before it keeps parsing; `taught !== false` then treats
   *  everything as taught, which is the pre-existing behaviour. */
  subject_name?: string
  taught?: boolean
}

interface StudentProgramme {
  id: string; priority: number; period?: string
  programme: {
    id: string; name: string; university: string
    exam_source_institution?: string
    university_max_options?: number
    is_placeholder?: boolean
    periods_available?: string[]
    requirements?: ProgrammeRequirement[]
  }
}

function programmeSourceInstitution(programme: StudentProgramme): string {
  return programme.programme.exam_source_institution || programme.programme.university
}

function sourceInstitutionForUniversity(programmes: StudentProgramme[], university: string): string {
  return programmes.find((programme) => programme.programme.university === university)?.programme.exam_source_institution
    || university
}

function universitiesForSourceInstitution(programmes: StudentProgramme[], sourceInstitution: string): string[] {
  return [...new Set(
    programmes
      .filter((programme) => programmeSourceInstitution(programme) === sourceInstitution)
      .map((programme) => programme.programme.university),
  )]
}

import { SUBJECT_EMOJI as EMOJI, SUBJECT_HUE as COLOR, SUBJECT_HUE_CLS as CLS } from '../lib/subjectPalette'
import { masteryLabel } from '../lib/masteryLabel'
import { DailyMissionCard } from '../components/DailyMissionCard'
import { GuidanceStrip, type GuidanceStripState } from '../components/GuidanceStrip'
import { MotivationalBanner } from '../components/MotivationalBanner'
import { pickExamNudge } from '../lib/motivationalSayings'

// Greeting follows a streak-first rule, then falls back to time-of-day.
function greetingFor(
  name: string,
  hour: number,
  streakDays: number,
  gender: 'F' | 'M' | 'X' | null | undefined,
): string {
  const firstName = visibleFirstName(name)
  const showName = !!firstName

  if (streakDays >= 7) {
    return showName
      ? `${streakDays} dias seguidos, ${firstName}. Estás bem forte.`
      : `${streakDays} dias seguidos. Estás bem forte.`
  }

  const hasAgreement = gender === 'F' || gender === 'M'
  const ready = gender === 'F' ? 'preparada' : 'preparado'

  if (hour < 12) {
    return hasAgreement
      ? (showName ? `Bom dia, ${firstName} - ${ready} para mais um dia?` : `Bom dia - ${ready} para mais um dia?`)
      : (showName ? `Bom dia, ${firstName}. Vamos lá começar.` : 'Bom dia. Vamos lá começar.')
  }
  if (hour < 18) {
    return showName ? `Boa tarde, ${firstName} - vamos a isso.` : 'Boa tarde - vamos a isso.'
  }
  return showName ? `Boa noite, ${firstName} - tens 10 minutos? Vamos avançar.` : 'Boa noite - tens 10 minutos? Vamos avançar.'
}

function maputoHour(): number {
  const fmt = new Intl.DateTimeFormat('pt-PT', {
    timeZone: 'Africa/Maputo',
    hour: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(new Date())
  const hourPart = parts.find(p => p.type === 'hour')?.value ?? '0'
  return parseInt(hourPart, 10)
}

const SUBJECTS_AUTH_REFRESH_KEY = 'subjects.auth_refresh_at'
const PRACTICE_WRITING_NUDGE_KEY_PREFIX = 'practice_writing_nudge_seen'
const grade12PopupDismissalKey  = (id: string) => `grade12_popup_dismissed_${id}`
const AUTH_REFRESH_TTL_MS       = 2 * 60_000
const AUX_TTLS_MS = {
  streak:     5 * 60_000,
  examConfig: 15 * 60_000,
  features:   30 * 60_000,
  placement:  10 * 60_000,
} as const

type AuxCachePayload = {
  streak?: { current_days: number; is_protected?: boolean }
  examConfigsByMode?: Record<string, Record<string, { duration_minutes: number; question_count: number }>>
  features?: { offline_packs: boolean; pack_questions_limit: number; questions_per_day: number }
  placement?: { placement_complete: boolean; subjects: { code: string; completed: boolean }[] }
  fetchedAt?: Partial<Record<'streak' | 'examConfig:admission' | 'examConfig:national_12' | 'features' | 'placement', number>>
}

function readSubjectsAuxCache(): AuxCachePayload {
  try {
    const raw = sessionStorage.getItem(SUBJECTS_AUX_CACHE_KEY)
    return raw ? JSON.parse(raw) as AuxCachePayload : {}
  } catch {
    return {}
  }
}

function writeSubjectsAuxCache(mutate: (current: AuxCachePayload) => AuxCachePayload) {
  try {
    const next = mutate(readSubjectsAuxCache())
    sessionStorage.setItem(SUBJECTS_AUX_CACHE_KEY, JSON.stringify(next))
  } catch {
    /* cache is best-effort */
  }
}

export const SubjectSelectPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [subjects, setSubjects]                   = useState<Subject[]>([])
  const [subjectGate, setSubjectGate]             = useState<SubjectGate | null>(null)
  // Weekly recap (PR 3.1) - directional capsule above the subject
  // grid. Backend computes from MasteryHistory + StudentProgress; we
  // hide the capsule entirely when there's no meaningful signal yet
  // (improved + risky + next_action all empty/null).
  const [weeklyRecap, setWeeklyRecap]              = useState<WeeklyRecap | null>(null)
  const [weeklyRecapDismissed, setWeeklyRecapDismissed] = useState(false)
  // Anonymous-first welcome card (Slice 11 + 12, 2026-05-19).
  // useAuth (Firebase path) and the demo claim helper write
  // `acesso_just_claimed` to localStorage the moment the anonymous
  // row flips to authenticated. We consume the flag once on mount
  // and render a centered welcome MOMENT (not a fleeting toast) that:
  //   - acknowledges the questions kept across the identity flip
  //   - names the programme + university the student picked during
  //     onboarding ("Vamos ajudar-te a entrar em X na Y")
  //   - has a single CTA that closes it and returns the student to
  //     the subject grid
  // Programme + university are pulled from `studentProgrammes` once
  // the /subjects fetch completes, so the card may render with just
  // the welcome line on the first paint and gain the programme
  // detail on the next render - fine; the message is honest either
  // way.
  const [showClaimWelcome, setShowClaimWelcome] = useState<{ questions_kept: number } | null>(null)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('acesso_just_claimed')
      if (!raw) return
      localStorage.removeItem('acesso_just_claimed')
      const parsed = JSON.parse(raw) as { questions_kept?: number }
      const n = typeof parsed?.questions_kept === 'number' ? parsed.questions_kept : 0
      setShowClaimWelcome({ questions_kept: n })
    } catch { /* localStorage disabled - card just won't fire */ }
  }, [])
  const [guidanceState, setGuidanceState]          = useState<GuidanceStripState>('loading')
  const [pickerOpen, setPickerOpen]               = useState(false)
  // Cooldown-elapsed prompt — fired by useEffect below once per (student,
  // cooldown cycle) the first time the page mounts after can_swap_now
  // flips to true. The student picks "Manter as mesmas" (dismiss) or
  // "Trocar disciplinas" (opens picker). Either path persists the
  // dismissal key.
  const [cooldownPromptOpen, setCooldownPromptOpen] = useState(false)
  const [studentProgrammes, setStudentProgrammes] = useState<StudentProgramme[]>([])
  const [hasChosen, setHasChosen]                 = useState(false)
  const [loading, setLoading]                     = useState(true)
  const [error, setError]                         = useState<string | null>(null)
  // Offline UX PR (2026-05-01) - when the /subjects fetch fails due to
  // OfflineError, we hydrate `subjects` from the IndexedDB pack-meta and
  // flip this flag. The render branches into a stripped-down offline
  // view (cached subjects only, no banner chrome) before the main
  // online render. See the early `if (isOfflineMode) return ...` below.
  const [isOfflineMode, setIsOfflineMode]         = useState(false)
  // Captured alongside the offline-mode flag so the offline render
  // surface can show pack age, total questions, programme context etc.
  // - info richer than what the synthetic Subject[] preserves.
  const [offlinePackMeta, setOfflinePackMeta]     = useState<PackMeta | null>(null)
  const [streak, setStreak]                       = useState<number>(0)
  const [streakIsProtected, setStreakIsProtected] = useState<boolean>(false)
  // Exam-nudge banner (Companion-identity gamification PR, 2026-05-03).
  // Founder correction (2026-05-19): never tell a student they have
  // "base suficiente" for a Mini Exame off a tiny sample. Require a
  // real per-subject practice base first.
  const [examNudgeMessage] = useState<string | null>(() =>
    Math.random() < 0.3 ? pickExamNudge() : null,
  )
  const [lockedSubject, setLockedSubject]         = useState<Subject | null>(null)
  // Free-path line shown alongside the upgrade modal, e.g. the next swap
  // date. Null when the lock has no free way out.
  const [lockedDetail, setLockedDetail]           = useState<string | null>(null)
  const [placementPending, setPlacementPending]   = useState(false)
  const [firstPlacementCode, setFirstPlacementCode] = useState<string | null>(null)
  const [studyMode, setStudyMode]                 = useState<'practice' | 'mini' | 'exam'>('practice')
  const [programmeListExpanded, setProgrammeListExpanded] = useState(false)
  // Which subject cards have their topics list expanded past the top 4.
  // Set of subject codes; collapsed by default so the card stays compact.
  const [expandedTopicSubjects, setExpandedTopicSubjects] = useState<Set<string>>(new Set())
  const [fullLockedSubject, setFullLockedSubject] = useState<Subject | null>(null)
  const [miniSubject, setMiniSubject]             = useState<Subject | null>(null)
  // Weekly mini-exam cap. Until now this wall was a disabled button with
  // no explanation and no way out; mini_exam_weekly_cap is 2 on free and
  // unlimited on both paid tiers, so paying genuinely lifts it.
  const [miniLockedSubject, setMiniLockedSubject] = useState<Subject | null>(null)
  // Name of the tier ABOVE the student's current one, for the monthly
  // full-exam cap. Derived from /subscription/offer rather than hardcoded:
  // which tier lifts a cap is admin config, and hardcoding it is exactly
  // what left UpgradePrompt selling "Plano Essencial" long after that tier
  // stopped existing. Fetched only when the modal actually opens, since
  // this wall is reachable by one tier and rare even there.
  const [nextTierName, setNextTierName] = useState<string | null>(null)
  const [toast, setToast]                         = useState<{ message: string; type: 'info' | 'warning' | 'error' } | null>(null)
  const showToast = (message: string, type: 'info' | 'warning' | 'error' = 'info') => setToast({ message, type })
  const { quota, refresh: refreshQuota }          = useSessionQuota()
  const miniAllowed = canStartMiniFrom(quota)

  useEffect(() => {
    if (!fullLockedSubject || !PAID_TIERS_ENABLED) return
    if (quota?.full.locked_forever || nextTierName || !quota?.tier_code) return
    let cancelled = false
    getOffer()
      .then(offer => {
        // offers arrive in sort_order and exclude the free default, so the
        // tier after the student's own is the next one up.
        const i = offer.offers.findIndex(o => o.tier_code === quota.tier_code)
        const next = i >= 0 ? offer.offers[i + 1] : undefined
        if (!cancelled) setNextTierName(next?.tier_name ?? null)
      })
      .catch(() => { /* no upsell line, the modal still explains the cap */ })
    return () => { cancelled = true }
  }, [fullLockedSubject, quota?.full.locked_forever, quota?.tier_code, nextTierName])
  const fullAllowed = canStartFullFrom(quota)
  const [examConfigMap, setExamConfigMap]         = useState<Record<string, { duration_minutes: number; question_count: number }>>({})
  const [features, setFeatures]                   = useState<{ offline_packs: boolean; pack_questions_limit: number; questions_per_day: number } | null>(null)
  const [pendingPracticeHref, setPendingPracticeHref] = useState<string | null>(null)

  const [detailsProgId, setDetailsProgId]         = useState<string | null>(null)
  // Discovery eligibility - gates the "Descobre cursos compatíveis"
  // banner. Once a student has run the CAT once, the banner is hidden
  // entirely (entry to /recommendations is via navigation from /profile
  // or by direct URL). Subscription override (premium can repeat) is a
  // future TODO. `null` = loading / unknown - banner stays as-is until
  // we know.
  const [discoveryCanRun, setDiscoveryCanRun] = useState<boolean | null>(null)
  useEffect(() => {
    // Phase 4 (2026-06-12): defer behind requestIdleCallback so this
    // non-critical eligibility check doesn't compete with /subjects for
    // the 3G bandwidth on first load. The discovery banner is a small
    // chrome element — students don't need it before the subject grid
    // is interactive. Matches the deferred pattern used by the streak,
    // features and placement aux fetches further down.
    let cancelled = false
    const fire = () => {
      api.get<{ can_run: boolean }>('/discovery/eligibility')
        .then(d => { if (!cancelled) setDiscoveryCanRun(d.can_run) })
        .catch(() => { if (!cancelled) setDiscoveryCanRun(true) })
    }
    if ('requestIdleCallback' in window) {
      const id = (window as Window & typeof globalThis).requestIdleCallback(fire, { timeout: 2000 })
      return () => {
        cancelled = true
        ;(window as Window & typeof globalThis).cancelIdleCallback(id)
      }
    }
    const t = setTimeout(fire, 400)
    return () => { cancelled = true; clearTimeout(t) }
  }, [])

  // University filter - only active when student spans multiple exam groups.
  // Persisted in sessionStorage so navigating back from /questions or /exam
  // keeps the pill the student was just using (target_university may disagree
  // with the student's active intent).
  const PILL_STORAGE_KEY = 'maza.selected_university'
  const [selectedUniversity, setSelectedUniversity] = useState<string | null>(() => {
    try { return sessionStorage.getItem(PILL_STORAGE_KEY) } catch { return null }
  })
  const navigate = useNavigate()
  const { user, logout, refresh } = useAuth()
  const authRefreshInFlight = useRef(false)
  // Subscription status - read from useAuth so we don't fire a second
  // /auth/me on every /subjects mount. Source of truth is /auth/me's
  // top-level `subscription`, surfaced through useAuth as user.subscription.
  // (Perf review Finding 1, 2026-05-05.)
  const isSubscribed = user?.subscription?.plan === 'premium' && user?.subscription?.status === 'active'

  const practiceWritingNudgeKey = (() => {
    const actorId = user?.id ?? 'guest'
    const day = new Date().toISOString().slice(0, 10)
    return `${PRACTICE_WRITING_NUDGE_KEY_PREFIX}_${actorId}_${day}`
  })()

  const shouldShowPracticeWritingNudge = () => {
    try {
      return localStorage.getItem(practiceWritingNudgeKey) !== '1'
    } catch {
      return true
    }
  }

  const markPracticeWritingNudgeSeen = () => {
    try {
      localStorage.setItem(practiceWritingNudgeKey, '1')
    } catch {
      /* best-effort */
    }
  }

  const continueToPractice = (href: string) => {
    if (shouldShowPracticeWritingNudge()) {
      setPendingPracticeHref(href)
      return
    }
    navigate(href)
  }

  const resumePracticeAfterNudge = () => {
    markPracticeWritingNudgeSeen()
    const href = pendingPracticeHref
    setPendingPracticeHref(null)
    if (href) navigate(href)
  }

  const shouldHoldInitialSubjectPickerForGrade12 = (() => {
    if (!subjectGate?.needs_initial_pick) return false
    if (subjectGate.kind === 'placeholder_explore') return false
    if (!user?.id) return false
    if (user.is_current_grade_12 !== null && user.is_current_grade_12 !== undefined) return false
    try {
      return !localStorage.getItem(grade12PopupDismissalKey(user.id))
    } catch {
      return true
    }
  })()

  const auxCacheFresh = (
    key: keyof NonNullable<AuxCachePayload['fetchedAt']>,
    ttlMs: number,
  ) => {
    const fetchedAt = readSubjectsAuxCache().fetchedAt?.[key] ?? 0
    return fetchedAt > 0 && (Date.now() - fetchedAt) < ttlMs
  }

  const markAuthRefresh = () => {
    try { sessionStorage.setItem(SUBJECTS_AUTH_REFRESH_KEY, String(Date.now())) } catch {}
  }

  const refreshAuthIfStale = async (force = false) => {
    if (authRefreshInFlight.current) return
    let shouldRefresh = force || !user
    if (!shouldRefresh) {
      try {
        const last = Number(sessionStorage.getItem(SUBJECTS_AUTH_REFRESH_KEY) ?? '0')
        shouldRefresh = !last || (Date.now() - last) > AUTH_REFRESH_TTL_MS
      } catch {
        shouldRefresh = true
      }
    }
    if (!shouldRefresh) return
    authRefreshInFlight.current = true
    try {
      await refresh()
      markAuthRefresh()
    } finally {
      authRefreshInFlight.current = false
    }
  }

  // Tier-1 email-verification banner state. Source of truth is
  // Firebase's auth.currentUser.emailVerified - no DB column needed.
  // DEMO_MODE has no Firebase user, so the banner never shows
  // (emailVerified initialises to true and the slot's IS_FIREBASE_ENABLED
  // guard short-circuits anyway).
  const [emailVerified, setEmailVerified] = useState<boolean>(() => {
    if (!IS_FIREBASE_ENABLED || !auth?.currentUser) return true
    return auth.currentUser.emailVerified
  })
  const [emailVerifyDismissedUntil, setEmailVerifyDismissedUntil] = useState<number>(0)
  const [emailVerifyResend, setEmailVerifyResend] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  useEffect(() => {
    if (!user?.id) return
    if (IS_FIREBASE_ENABLED && auth?.currentUser) {
      setEmailVerified(auth.currentUser.emailVerified)
    }
    try {
      setEmailVerifyDismissedUntil(Number(localStorage.getItem(`email_verify_dismissed_until_${user.id}`) ?? '0'))
    } catch { /* */ }
  }, [user?.id])
  const profileBannerDismissDays = user?.nudge_config?.profile_completion_dismiss_days ?? 14
  const grade12OptInDismissDays = user?.nudge_config?.grade12_opt_in_dismiss_days ?? 14
  const nameFixDismissDays = user?.nudge_config?.name_fix_dismiss_days ?? 30
  const emailVerifyDismissHours = user?.nudge_config?.email_verify_dismiss_hours ?? 24

  // Active mode for this view (DUAL_MODE_NATIONAL_12_PLAN.md §3.1).
  // Initial value pecking order:
  //   1. sessionStorage (the "sticky" value from the last toggle in this session)
  //   2. student.preferred_mode mapped: admission/national_12 stay; 'both'
  //      defaults to national_12 per Q1
  //   3. 'admission' for any unexpected value
  // The toggle UI is shown ONLY when preferred_mode='both' - single-mode
  // students change their mode via /profile, not here.
  const MODE_STORAGE_KEY = 'maza.active_mode'
  const [activeMode, setActiveMode] = useState<'admission' | 'national_12'>(() => {
    try {
      const urlMode = new URLSearchParams(window.location.search).get('mode')
      if (urlMode === 'admission' || urlMode === 'national_12') return urlMode
    } catch { /* fall through */ }
    try {
      const stored = sessionStorage.getItem(MODE_STORAGE_KEY)
      if (stored === 'admission' || stored === 'national_12') return stored
    } catch { /* fall through */ }
    const pref = user?.preferred_mode
    if (pref === 'national_12') return 'national_12'
    if (pref === 'both')        return 'national_12'   // Q1
    return 'admission'
  })
  // Persist the active mode in sessionStorage whenever it changes so back-
  // navigation from /questions or /exam picks the same view.
  useEffect(() => {
    try { sessionStorage.setItem(MODE_STORAGE_KEY, activeMode) } catch { /* no-op */ }
  }, [activeMode])
  // Keep activeMode in sync if the student's preferred_mode flips while
  // they're on this page (e.g. they hit /profile and changed it). The
  // sessionStorage value still wins if present.
  useEffect(() => {
    const urlMode = searchParams.get('mode')
    if (urlMode === 'admission' || urlMode === 'national_12') return
    if (!user) return
    if (user.preferred_mode === 'admission' && activeMode !== 'admission') {
      setActiveMode('admission')
    } else if (user.preferred_mode === 'national_12' && activeMode !== 'national_12') {
      setActiveMode('national_12')
    }
    // 'both' stays whatever the toggle was last set to.
  }, [user?.preferred_mode])  // eslint-disable-line react-hooks/exhaustive-deps

  // Bottom-nav PR (2026-05-02) - mode now lives in the bottom tab bar.
  // The tab bar navigates to /subjects?mode=admission or
  // /subjects?mode=national_12; SubjectSelectPage reads the param and
  // syncs activeMode to it. URL is the source of truth when present;
  // fallback to sessionStorage / preferred_mode logic above.
  useEffect(() => {
    const urlMode = searchParams.get('mode')
    if (urlMode === 'admission' && activeMode !== 'admission') {
      setActiveMode('admission')
    } else if (urlMode === 'national_12' && activeMode !== 'national_12') {
      setActiveMode('national_12')
    }
  }, [searchParams])  // eslint-disable-line react-hooks/exhaustive-deps

  // Canonicalise the URL when it arrives without ?mode=.
  //
  // Two components resolved the mode independently and disagreed. This page
  // falls back URL -> sessionStorage -> preferred_mode, where 'both' means
  // national_12 (Q1). StudentTabBar reads the query param alone and falls
  // back to a hardcoded 'admissao'. So a 'both' student landing on /subjects
  // with no param saw the 12ª grid with Admissão highlighted, and tapping any
  // tab "fixed" it by finally writing a param.
  //
  // Writing the resolved mode makes the URL the single source of truth for
  // everyone who reads it, rather than teaching the tab bar to repeat this
  // precedence and drift from it later. replace: true so it does not add a
  // history entry the back button has to walk through.
  useEffect(() => {
    if (searchParams.get('mode')) return
    const next = new URLSearchParams(searchParams)
    next.set('mode', activeMode)
    setSearchParams(next, { replace: true })
  }, [activeMode, searchParams, setSearchParams])

  // Mode toggle removed (bottom-nav PR, 2026-05-02). Mode is now a tab.
  // Kept the constant set to false in case lingering JSX references
  // need a flag while the in-page toggle JSX is being removed.
  const showModeToggle = false
  // 12ª view hides ranking surfaces, university pill, and the full-exam
  // tab (Decision #5 + §3.5 + Q3).
  const isNationalView = activeMode === 'national_12'
  useEffect(() => {
    if (user?.is_anonymous && studyMode !== 'practice') {
      setStudyMode('practice')
    }
  }, [user?.is_anonymous, studyMode])

  // (Removed 2026-05-10) The per-session "minhas disciplinas" exclusion
  // set used to live here, persisted in sessionStorage under
  // 'maza.n12_excluded_subjects'. It was a client-only filter and
  // didn't cross-cut rankings/insights/etc. Replaced by the persisted
  // school_subject_codes curriculum filter (Student.school_subject_codes,
  // edited from /profile → Disciplinas). The backend now ships the
  // already-narrowed subject list in /subjects when in national_12 mode,
  // so this page just renders what it gets.

  // 2026-05-01: removed the auto-snap-to-Prática effect that fired when
  // toggling into 12ª view. It dated from when "Exame Completo" was
  // hidden in 12ª (no INEE configs existed yet). Now INEE-12 ExamConfigs
  // exist and the routing supports 12ª full exams, so the snap silently
  // bounced the student back to Prática every time they tried to enter
  // exam mode. The toggle button + the per-subject CTA are the only
  // gates now - this effect is no longer needed.

  // Auto-open the free-tier picker on first visit when the gate is
  // active and the student hasn't picked yet (2026-04-28). The picker
  // is mandatory - student can't browse questions until they choose.
  useEffect(() => {
    if (!subjectGate) return
    if (shouldHoldInitialSubjectPickerForGrade12) return
    if (subjectGate.kind !== 'placeholder_explore' && user?.is_current_grade_12 !== true && activeMode !== 'national_12') return
    if (subjectGate.needs_initial_pick) setPickerOpen(true)
  }, [subjectGate?.needs_initial_pick, shouldHoldInitialSubjectPickerForGrade12, user?.is_current_grade_12, activeMode])

  // Cooldown-elapsed prompt (2026-06-09).
  //
  // Fires once per cooldown cycle when can_swap_now flips true and the
  // student has prior picks AND there was a real cooldown window that
  // just ended. The cooldown_days > 0 + picked_at gates were added
  // 2026-06-17 after reports that the prompt was firing for new
  // students: admin / premium tiers have cooldown_days=0 (free swaps
  // always allowed), which made can_swap_now permanently true and
  // surfaced the prompt every visit. Per founder rule, the cooldown
  // time belongs to the student — only show the prompt when a real
  // waiting period configured by admin has actually elapsed.
  //
  // Dismissal key includes picked_at so the next cooldown end re-fires
  // (the timestamp moves on a swap; add-only saves don't move it so the
  // student isn't re-prompted just because they topped up under-cap).
  useEffect(() => {
    if (!user?.id || !subjectGate) return
    if (subjectGate.cooldown_days <= 0) return  // no cooldown configured → no prompt, ever
    if (!subjectGate.picked_at) return          // no pick timestamp → no cycle to close
    if (!subjectGate.can_swap_now) return
    if (subjectGate.picked_codes.length === 0) return
    if (subjectGate.needs_initial_pick) return  // first-pick flow owns the surface
    if (pickerOpen) return
    if (cooldownPromptOpen) return
    const key = cooldownPromptDismissalKey(user.id, subjectGate.picked_at)
    try {
      if (localStorage.getItem(key) === '1') return
    } catch { /* private mode — re-prompt is acceptable */ }
    setCooldownPromptOpen(true)
  }, [
    user?.id,
    subjectGate?.cooldown_days,
    subjectGate?.can_swap_now,
    subjectGate?.picked_codes.length,
    subjectGate?.picked_at,
    subjectGate?.needs_initial_pick,
    pickerOpen,
    cooldownPromptOpen,
  ])

  const dismissCooldownPrompt = () => {
    setCooldownPromptOpen(false)
    if (user?.id && subjectGate) {
      try {
        localStorage.setItem(cooldownPromptDismissalKey(user.id, subjectGate.picked_at), '1')
      } catch { /* noop */ }
    }
  }

  // Keep `active_programme_id` in sync with the university pill (2026-04-28).
  //
  // Pre-fix the pill row and the "Praticar para X · UNI" button were
  // independent: clicking ISCAM filtered the subject grid but the button
  // kept showing "Medicina · UEM" because nothing wrote `active_programme_id`.
  // This effect closes the gap - when the user picks a uni pill in
  // admission view, we POST the highest-priority programme at that uni so
  // the button + the pill always agree.
  //
  // No-op cases (skip the POST):
  //   - 12ª view (button is hidden anyway)
  //   - no pill selected yet
  //   - user already has no programmes (nothing to switch to)
  //   - active_programme_id already belongs to selectedUniversity
  //   - student has zero programmes at the selected uni (defensive - pill
  //     row only renders unis the student commits to, but the data could
  //     race during reloadAll)
  useEffect(() => {
    if (isNationalView)         return
    if (!selectedUniversity)    return
    if (!user)                  return
    if (studentProgrammes.length === 0) return

    const realProgs = studentProgrammes.filter(sp => !sp.programme.is_placeholder)
    const active    = realProgs.find(sp => sp.programme.id === user.active_programme_id) ?? null
    if (active && active.programme.university === selectedUniversity) return

    // Pick the highest-priority real programme at the selected uni.
    const target = realProgs
      .filter(sp => sp.programme.university === selectedUniversity)
      .sort((a, b) => a.priority - b.priority)[0]
    if (!target) return

    api.post('/auth/switch-context', { programme_id: target.programme.id })
      .then(() => reloadAll())
      .catch(err => {
        // Soft-fail - the pill filter still narrows subjects via local
        // state, so the user isn't blocked. Log so we catch regressions.
        console.warn('[subjects] switch-context sync failed', err)
      })
    // reloadAll is stable; intentionally not a dep so we don't re-run on
    // every render after the POST resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUniversity, isNationalView, user?.active_programme_id, studentProgrammes])

  // Trigger a /subjects refresh alongside the /auth/me refresh so the switcher
  // and subject grid stay consistent after any v2 mutation.
  const [subjectsReloadKey, setSubjectsReloadKey] = useState(0)
  const reloadAll = () => {
    void refreshAuthIfStale(true)
    setSubjectsReloadKey(k => k + 1)
  }

  useEffect(() => {
    // Phase 0/2 (2026-06-12): instant render from localStorage cache when
    // available. The /subjects payload is moderately heavy (subjects +
    // student_programmes + subject_gate + weekly_recap) and can take
    // multiple seconds on 3G/2G. Stale-while-revalidate: render last-known
    // good snapshot synchronously, fire the real fetch in the background
    // (still through the same .then below), let it overwrite when it
    // arrives. Subjects page now renders < 100ms on every visit-after-
    // first.
    //
    // Telemetry fires once per mount-cycle of this effect so we can
    // measure the impact on real student sessions.
    const subjectsFetchStart = performance.now()
    const cached = readSubjectsCache<Subject, StudentProgramme, SubjectGate>(
      user?.id, activeMode, selectedUniversity,
    )
    if (cached) {
      setSubjects(cached.subjects)
      setStudentProgrammes(cached.studentProgrammes)
      setHasChosen(cached.hasChosen)
      setSubjectGate(cached.subjectGate)
      setLoading(false)
      trackAnalytics('subjects_page.cache_hit', {
        age_ms: Date.now() - cached.cachedAt,
        mode: activeMode,
        university: selectedUniversity ?? null,
        subjects: cached.subjects.length,
      })
    } else {
      setLoading(true)
    }
    trackAnalytics('subjects_page.mount', {
      cache_hit: !!cached,
      mode: activeMode,
      university: selectedUniversity ?? null,
    })
    // useAuth already revalidates on app load and most profile/programme
    // mutations explicitly refresh auth. Keep this page-level recheck
    // throttled so daily returns don't pay a fresh /auth/me every visit.
    void refreshAuthIfStale(subjectsReloadKey > 0)
    // Mode-aware subject list - admission shows 6, national_12 shows up
    // to 10. Backend re-derives the set per request based on ?mode=.
    // Pill-aware (PR follow-up 2026-05-05): when the student is on a
    // specific uni pill (multi-group commit), pass it through so the
    // per-topic counts narrow to that pill's source. Topics that
    // wouldn't have any answerable questions under the active pill
    // (e.g. UEM Variant II + Geometria-only-Variant-III) are then
    // hidden in the chip grid, matching the topic-drill route's
    // strict behaviour.
    const uniQs = selectedUniversity ? `&university=${encodeURIComponent(selectedUniversity)}` : ''
    api.get<{ subjects: Subject[]; student_programmes: StudentProgramme[]; has_chosen_programmes: boolean; subject_gate?: SubjectGate; weekly_recap?: WeeklyRecap | null }>(`/subjects?mode=${activeMode}${uniQs}`)
      .then((d) => {
        // Phase 3: diff-aware merge. When we rendered the page from
        // cache and the revalidation comes back with IDENTICAL data,
        // skip setState entirely so React doesn't waste a render pass
        // (no flicker, no scroll jump, no child reconciliation). The
        // weekly_recap and other "always refresh" pieces are NOT in
        // the diff — they're set unconditionally below since they're
        // not cached.
        const incoming = {
          subjects: d.subjects,
          studentProgrammes: d.student_programmes,
          hasChosen: d.has_chosen_programmes,
          subjectGate: d.subject_gate ?? null,
        }
        const noChange = cached !== null && subjectsPayloadEquals<Subject, StudentProgramme, SubjectGate>(cached, incoming)

        if (!noChange) {
          setSubjects(d.subjects)
          setStudentProgrammes(d.student_programmes)
          setHasChosen(d.has_chosen_programmes)
          setSubjectGate(d.subject_gate ?? null)
        }
        setWeeklyRecap(d.weekly_recap ?? null)
        setLoading(false)
        // Persist the just-fetched shape so the next mount can render
        // instantly from cache. Skipped silently when user.id is missing
        // (anonymous bootstrap edge) — the lib also guards. Skipped on
        // no-change too since writing the same JSON back is wasted IO.
        if (user?.id && !noChange) {
          writeSubjectsCache<Subject, StudentProgramme, SubjectGate>({
            userId: user.id,
            mode: activeMode,
            selectedUniversity,
            ...incoming,
          })
        }
        trackAnalytics('subjects_page.network_load_done', {
          duration_ms: Math.round(performance.now() - subjectsFetchStart),
          cache_hit: !!cached,
          no_change: noChange,
          mode: activeMode,
          university: selectedUniversity ?? null,
          subjects: d.subjects.length,
        })

        // Auto-select university when student spans multiple exam groups
        const realProgs = d.student_programmes.filter(sp => !sp.programme.is_placeholder)
        const unis = [...new Set(realProgs.map(sp => sp.programme.university))]
        const groups = [...new Set(realProgs.map(programmeSourceInstitution))]
        const isMulti = groups.length > 1
        let prefetchUni: string | null = null
        if (isMulti) {
          setSelectedUniversity(prev => {
            if (prev) { prefetchUni = prev; return prev } // keep existing selection across reloads
            // Default to the university matching target_university, or first
            const targetGroup = user?.target_university
              ? sourceInstitutionForUniversity(realProgs, user.target_university)
              : null
            const defaultUni = unis.find(u => sourceInstitutionForUniversity(realProgs, u) === targetGroup) ?? unis[0]
            prefetchUni = defaultUni ?? null
            return defaultUni ?? null
          })
        }

        const readinessRun = () => {
          // POST /offline-readiness/fill carries enforceNonAnonymous, so for a
          // guest this is a guaranteed 403 that also trips the retry path in
          // offlineReadiness.ts. useOfflineReadiness already excludes them via
          // isStudentSurface; this call site did not.
          if (user?.is_anonymous) return
          // One fill per surface the student can open, from the same
          // predicate the tab bar uses. Admission-only students resolve to a
          // single fill and behave exactly as before.
          for (const mode of availableModes(user?.preferred_mode, user?.is_current_grade_12)) {
            void ensureOfflineReadiness({
              priority: 'background',
              mode,
              // University scoping is admission-only; the backend pins
              // national_12 to INEE regardless.
              university: mode === 'admission' && isMulti ? prefetchUni : null,
              activeSubjectCode: user?.active_subject ?? d.subjects[0]?.code ?? null,
              subjectCodes: d.subjects.map(s => s.code),
              studentId: user?.id ?? null,
            })
          }
        }
        if (navigator.onLine) {
          if ('requestIdleCallback' in window) {
            ;(window as Window & typeof globalThis).requestIdleCallback(readinessRun, { timeout: 2000 })
          } else {
            setTimeout(readinessRun, 800)
          }
        }
      })
      .catch(async (e: Error) => {
        trackAnalytics('subjects_page.network_load_failed', {
          duration_ms: Math.round(performance.now() - subjectsFetchStart),
          cache_hit: !!cached,
          mode: activeMode,
          university: selectedUniversity ?? null,
          error: e?.constructor?.name ?? 'Error',
        })
        // Offline UX PR (2026-05-01) - graceful degradation when the
        // device is offline. Build a synthetic Subject[] from the
        // IndexedDB pack-meta so the student can still navigate into
        // cached subjects without seeing a generic "Load failed".
        // Subjects without a downloaded pack are not surfaced in the
        // offline view; they re-appear when connection returns.
        if (e instanceof OfflineError) {
          const meta = await getPackMeta()
          if (meta && meta.subjects.length > 0) {
            const offlineSubjects: Subject[] = meta.subjects.map(s => ({
              id:                       s.code,            // synthetic - IDB doesn't store the real id
              code:                     s.code,
              name:                     s.name,
              description:              null,
              requires_subscription:    false,
              unlocked:                 true,              // downloaded ⇒ unlocked, regardless of tier
              topics:                   [],
              required_for_programmes:  [],
              subject_weight:           0,
              exam_variant:             '',
              mastery_score:            null,
            }))
            setSubjects(offlineSubjects)
            setOfflinePackMeta(meta)
            setIsOfflineMode(true)
            setLoading(false)
            return
          }
          // Offline AND no pack - render the same "you're offline" message
          // the OfflineGate uses elsewhere. We reuse the error-state slot
          // because there's nothing useful to render in the main grid.
          setError('offline')
          setIsOfflineMode(true)
          setOfflinePackMeta(null)
          setLoading(false)
          return
        }
        setError(e.message)
        setLoading(false)
      })

    // selectedUniversity included so re-fetching narrows the per-topic
    // counts when the student switches uni pill - the chip grid hides
    // topics that have no answerable questions under the new pill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectsReloadKey, activeMode, selectedUniversity])

  // Non-critical fetches - deferred behind requestIdleCallback so the
  // critical /subjects path above wins the network slot on 3G.
  // (Perf review Finding 1, 2026-05-05.)
  // None of these gate first-meaningful-paint:
  //   - streak       → header pill
  //   - exam-configs → "Exame Completo" buttons (below the fold)
  //   - features     → offline pack card (below the fold)
  //   - placement    → nudge banner
  // The duplicate /auth/me read for `isSubscribed` was removed - the
  // value is now derived from useAuth's user.subscription.
  // selectedUniversity is intentionally NOT in this dep array: none
  // of these endpoints depend on the uni pill, so re-firing them on
  // every pill click was wasted load.
  useEffect(() => {
    const cached = readSubjectsAuxCache()
    if (cached.streak && auxCacheFresh('streak', AUX_TTLS_MS.streak)) {
      setStreak(cached.streak.current_days)
      setStreakIsProtected(cached.streak.is_protected === true)
    }
    if (cached.features && auxCacheFresh('features', AUX_TTLS_MS.features)) {
      setFeatures(cached.features)
    }
    if (cached.placement && auxCacheFresh('placement', AUX_TTLS_MS.placement)) {
      if (!cached.placement.placement_complete && cached.placement.subjects.length > 0) {
        setPlacementPending(true)
        const next = cached.placement.subjects.find(s => !s.completed)
        if (next) setFirstPlacementCode(next.code)
      } else {
        setPlacementPending(false)
        setFirstPlacementCode(null)
      }
    }

    const fire = () => {
      // GET /streaks/me is enforceNonAnonymous. A guest has no streak to show.
      if (user?.is_anonymous) return
      if (!auxCacheFresh('streak', AUX_TTLS_MS.streak) || subjectsReloadKey > 0) {
        api.get<{ current_days: number; is_protected?: boolean }>('/streaks/me')
        .then(d => {
          setStreak(d.current_days)
          setStreakIsProtected(d.is_protected === true)
          writeSubjectsAuxCache(current => ({
            ...current,
            streak: d,
            fetchedAt: { ...current.fetchedAt, streak: Date.now() },
          }))
        })
        .catch(() => {})
      }

      if (!auxCacheFresh('features', AUX_TTLS_MS.features) || subjectsReloadKey > 0) {
        api.get<{ features: { offline_packs: boolean; pack_questions_limit: number; questions_per_day: number } }>('/students/me/features')
          .then(d => {
            setFeatures(d.features)
            writeSubjectsAuxCache(current => ({
              ...current,
              features: d.features,
              fetchedAt: { ...current.fetchedAt, features: Date.now() },
            }))
          })
          .catch(() => {})
      }

      if (!auxCacheFresh('placement', AUX_TTLS_MS.placement) || subjectsReloadKey > 0) {
        api.get<{ placement_complete: boolean; subjects: { code: string; completed: boolean }[] }>('/placement/status')
          .then(d => {
            writeSubjectsAuxCache(current => ({
              ...current,
              placement: d,
              fetchedAt: { ...current.fetchedAt, placement: Date.now() },
            }))
            if (!d.placement_complete && d.subjects.length > 0) {
              setPlacementPending(true)
              const next = d.subjects.find(s => !s.completed)
              if (next) setFirstPlacementCode(next.code)
            } else {
              setPlacementPending(false)
              setFirstPlacementCode(null)
            }
          })
          .catch(() => {})
      }
    }
    if ('requestIdleCallback' in window) {
      const id = (window as Window & typeof globalThis).requestIdleCallback(fire, { timeout: 2000 })
      return () => (window as Window & typeof globalThis).cancelIdleCallback(id)
    }
    const t = setTimeout(fire, 400)
    return () => clearTimeout(t)
  }, [subjectsReloadKey])

  useEffect(() => {
    const examConfigKey = `examConfig:${activeMode}` as const
    const cachedMap = readSubjectsAuxCache().examConfigsByMode?.[activeMode]
    if (cachedMap && auxCacheFresh(examConfigKey, AUX_TTLS_MS.examConfig)) {
      setExamConfigMap(cachedMap)
    }

    const fire = () => {
      // GET /exam-configs/available is enforceNonAnonymous. A guest cannot
      // start an Exame Completo anyway (exam_simulation is false on their
      // tier), so the map would go unused even if the call succeeded.
      if (user?.is_anonymous) return
      if (cachedMap && auxCacheFresh(examConfigKey, AUX_TTLS_MS.examConfig)) return
      // 2026-05-01: pass ?mode= so 12ª students get INEE_GROUP configs and
      // their "Exame Completo" buttons render.
      api.get<{ configs: { subject: { code: string }; duration_minutes: number; question_count: number }[] }>(`/exam-configs/available?mode=${activeMode}`)
        .then(d => {
          const map: Record<string, { duration_minutes: number; question_count: number }> = {}
          d.configs.forEach(c => { map[c.subject.code] = { duration_minutes: c.duration_minutes, question_count: c.question_count } })
          setExamConfigMap(map)
          writeSubjectsAuxCache(current => ({
            ...current,
            examConfigsByMode: { ...(current.examConfigsByMode ?? {}), [activeMode]: map },
            fetchedAt: { ...current.fetchedAt, [examConfigKey]: Date.now() },
          }))
        })
        .catch(() => {})
    }

    if ('requestIdleCallback' in window) {
      const id = (window as Window & typeof globalThis).requestIdleCallback(fire, { timeout: 2000 })
      return () => (window as Window & typeof globalThis).cancelIdleCallback(id)
    }
    const t = setTimeout(fire, 400)
    return () => clearTimeout(t)
  }, [activeMode])

  const showPracticeSupport = studyMode === 'practice'
  const isPlaceholderExploreGate = subjectGate?.kind === 'placeholder_explore'
  const gateLockedActionLabel = isPlaceholderExploreGate ? '🔒 Escolher disciplinas' : '🔒 Trocar disciplina'
  const gateLockedToast = isPlaceholderExploreGate
    ? `Escolhe até ${subjectGate?.limit ?? 2} disciplinas enquanto ainda não escolheste um curso.`
    : 'Esta disciplina está bloqueada no teu plano.'
  const weeklyImproved = weeklyRecap?.improved ?? []
  const weeklyRisky = weeklyRecap?.risky ?? []
  const weeklyNextAction = weeklyRecap?.next_action ?? null
  const weeklyRecapVisible =
    showPracticeSupport
    && !isNationalView
    && !weeklyRecapDismissed
    && guidanceState !== 'active_mission'
    && guidanceState !== 'mission_done'
    && (weeklyImproved.length > 0 || weeklyRisky.length > 0 || !!weeklyNextAction)

  useEffect(() => {
    setWeeklyRecapDismissed(false)
  }, [
    activeMode,
    studyMode,
    weeklyImproved[0]?.subject_code,
    weeklyImproved[0]?.delta,
    weeklyRisky[0]?.subject_code,
    weeklyRisky[0]?.delta,
    weeklyNextAction?.subject_code,
    weeklyNextAction?.copy,
  ])

  useEffect(() => {
    if (!weeklyRecapVisible) return
    const t = window.setTimeout(() => setWeeklyRecapDismissed(true), 10_000)
    return () => window.clearTimeout(t)
  }, [weeklyRecapVisible])

  if (loading) return <SubjectPageSkeleton />

  // Offline UX PR (2026-05-01) - dedicated offline view rendered when
  // the /subjects fetch fell back to the IndexedDB pack. We deliberately
  // skip all the online-only chrome (banners, programme picker, exam
  // configs, study path) because none of those features have a sensible
  // offline state - better to show a clean, focused "your downloaded
  // subjects" view than a half-broken version of the online layout.
  if (isOfflineMode) {
    // Mode is read from the cached useAuth user (no API call needed).
    // The pack was downloaded under whichever mode was active at the
    // time, so showing user.preferred_mode is the best-effort "what
    // is this pack for" signal until we plumb mode into PackMeta.
    const preferredMode = user?.preferred_mode ?? 'admission'
    const isDualMode    = preferredMode === 'both'
    const activeModeLabel =
      preferredMode === 'national_12' ? '12ª Classe - Exame Nacional'
        : preferredMode === 'admission'   ? 'Admissão Universitária'
        : '12ª Classe + Admissão'

    // Pack age - relative time string, mirrors the format used in the
    // online OfflinePackCard so students see consistent age framing.
    const packAgeText = (() => {
      if (!offlinePackMeta) return null
      const ms = Date.now() - new Date(offlinePackMeta.downloadedAt).getTime()
      const days = Math.floor(ms / 86_400_000)
      if (days >= 7) {
        const w = Math.floor(days / 7)
        return `há ${w} semana${w === 1 ? '' : 's'}`
      }
      if (days >= 1) return `há ${days} dia${days === 1 ? '' : 's'}`
      const hours = Math.floor(ms / 3_600_000)
      if (hours >= 1) return `há ${hours} hora${hours === 1 ? '' : 's'}`
      return 'há instantes'
    })()

    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => navigate('/profile')}
              className="text-sm text-gray-500 hover:text-gray-700"
              aria-label="Perfil"
            >
              ←
            </button>
            <span className="font-semibold text-gray-800 text-sm">Disciplinas</span>
            <div className="w-6" />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
          {/* Offline status strip */}
          <section className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className="text-xl shrink-0" aria-hidden>📵</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Estás offline</p>
              <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                {offlinePackMeta
                  ? <>As tuas respostas serão sincronizadas quando voltares a estar online.</>
                  : <>Ainda não tens questões offline. Liga à internet - guardamos automaticamente.</>}
              </p>
            </div>
          </section>

          {offlinePackMeta ? (
            <>
              {/* Mode + pack metadata card. Mode is shown prominently
                  because the pack is mode-scoped - student needs to
                  know they're studying for X, not Y. */}
              <section className="bg-white rounded-xl border border-gray-100 px-5 py-4">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">
                  Modo de estudo
                </p>
                <p className="text-base font-semibold text-gray-900">{activeModeLabel}</p>
                {isDualMode && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Tens dois modos activos. Liga à internet para alternar entre eles.
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-baseline justify-between text-xs">
                  <span className="text-gray-500">
                    <span className="font-semibold text-gray-700">{offlinePackMeta.subjects.length}</span> disciplina{offlinePackMeta.subjects.length === 1 ? '' : 's'}
                    {' · '}
                    <span className="font-semibold text-gray-700">{offlinePackMeta.totalQuestions}</span> questões
                  </span>
                  {packAgeText && (
                    <span className="text-gray-400">Actualizado {packAgeText}</span>
                  )}
                </div>
                {offlinePackMeta.programmeNames.length > 0 && (
                  <p className="text-[11px] text-gray-400 mt-2 leading-snug">
                    Em preparação: {offlinePackMeta.programmeNames.join(' · ')}
                  </p>
                )}
              </section>

              {/* Subject grid with question counts and clear CTA */}
              <section>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2 px-1">
                  Disciplinas
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {offlinePackMeta.subjects.map(s => (
                    <button
                      key={s.code}
                      onClick={() => continueToPractice(`/questions?subject=${s.code}`)}
                      className="text-left bg-white hover:bg-gray-50 border border-gray-100 rounded-xl p-4 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-lg font-semibold text-gray-900">{s.code}</span>
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                          📥
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 leading-snug mb-2">{s.name}</p>
                      <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-gray-100">
                        <span className="text-[11px] text-gray-500">
                          {s.count} questões
                        </span>
                        <span className="text-xs font-semibold text-indigo-600">
                          Praticar →
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>

              {/* Honest disclosure: not everything works offline */}
              <section className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  <span className="font-semibold">Disponível offline:</span> prática livre.
                  <br />
                  <span className="font-semibold">Precisa de internet:</span> Mini-exames, Exame Completo, Tutor, recomendações.
                </p>
              </section>

              {/* Bottom action - manage downloads */}
              <button
                onClick={() => navigate('/profile?section=offline')}
                className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                Gerir descarregamentos →
              </button>
            </>
          ) : (
            <section className="bg-white rounded-xl border border-gray-100 px-5 py-8 text-center">
              <div className="text-4xl mb-3">📥</div>
              <p className="text-sm font-semibold text-gray-900 mb-1">Sem questões offline</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Liga à internet - guardamos automaticamente as questões para usares offline.
              </p>
            </section>
          )}
        </main>
      </div>
    )
  }

  if (error)   return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-red-500 text-sm">{error}</div></div>

  // Derive multi-university state. National_12 view ignores all of this
  // (Decision #3 - national exam isn't institution-scoped).
  const realProgrammes    = studentProgrammes.filter(sp => !sp.programme.is_placeholder)

  // Subject codes required by the student's CURRENTLY-committed programmes
  // (2026-05-24). Drives the "this is yours" ring + sort priority in
  // BOTH modes. Pre-fix the 12ª ring used school_subject_codes (Layer A
  // chip picker), which stayed sticky after the student uncommitted
  // from courses — rings showed for stale requirements. This set
  // rebuilds on every render from the live programmes data, so
  // uncommitting a programme removes the ring immediately.
  const activeRequiredSubjects = new Set<string>(
    realProgrammes.flatMap(sp => sp.programme.requirements?.map(r => r.subject_code) ?? []),
  )
  const studentUnis       = [...new Set(realProgrammes.map(sp => sp.programme.university))]
  const studentGroups     = [...new Set(realProgrammes.map(programmeSourceInstitution))]
  const isMultiGroup      = !isNationalView && studentGroups.length > 1

  // When multi-group and a university is selected, build per-group subjects from
  // programme requirements (correct variant per group, correct weight).
  // Otherwise fall back to the merged /subjects response as before.
  let visibleSubjects: Subject[]
  let activeVariantFor: Record<string, string> = {}   // subjectCode → variant for selected uni

  if (isNationalView) {
    // 12ª view: backend now ships the FULL national subject list again.
    // The student's chosen/free subjects show as unlocked; the rest stay
    // visible and can render locked so the catalogue remains legible.
    // We just strip the admission-only metadata (§3.5).
    //
    // Sort (2026-05-24): unlocked first, then within unlocked the
    // subjects required by ACTIVE programme commits float to the top
    // (so a student preparing for both 12ª and an admission programme
    // sees their committed-programme subjects up front), finally
    // alphabetical. Pre-fix sort was just (locked, code) which buried
    // the student's most relevant subjects somewhere in the alphabet.
    visibleSubjects = subjects
      .map(s => ({
        ...s,
        subject_weight:          0,
        exam_variant:            '',
        required_for_programmes: [],
      }))
      .sort((a, b) => {
        const aLocked = !!a.locked
        const bLocked = !!b.locked
        if (aLocked !== bLocked) return aLocked ? 1 : -1
        const aReq = activeRequiredSubjects.has(a.code)
        const bReq = activeRequiredSubjects.has(b.code)
        if (aReq !== bReq) return aReq ? -1 : 1
        return a.code.localeCompare(b.code)
      })
  } else if (isMultiGroup && selectedUniversity) {
    const selectedGroup = sourceInstitutionForUniversity(realProgrammes, selectedUniversity)
    const groupUnis     = universitiesForSourceInstitution(realProgrammes, selectedGroup)
    // Programmes at the selected group
    const groupProgs    = realProgrammes.filter(sp => groupUnis.includes(sp.programme.university))

    // Build subject codes + variants from their requirements
    const reqMap: Record<string, { weight: number; variant: string; programmes: string[] }> = {}
    for (const sp of groupProgs) {
      for (const r of (sp.programme.requirements ?? [])) {
        if (!reqMap[r.subject_code]) {
          reqMap[r.subject_code] = { weight: r.weight, variant: r.variant, programmes: [] }
        } else if (r.weight > reqMap[r.subject_code].weight) {
          reqMap[r.subject_code].weight  = r.weight
          reqMap[r.subject_code].variant = r.variant
        }
        const label = `${sp.programme.university} · ${sp.programme.name}`
        if (!reqMap[r.subject_code].programmes.includes(label))
          reqMap[r.subject_code].programmes.push(label)
      }
    }
    activeVariantFor = Object.fromEntries(Object.entries(reqMap).map(([code, m]) => [code, m.variant]))

    // Match back to full Subject objects from the API (for topics, mastery, etc.)
    visibleSubjects = subjects
      .filter(s => reqMap[s.code])
      .map(s => ({
        ...s,
        subject_weight:          reqMap[s.code].weight,
        exam_variant:            reqMap[s.code].variant,
        required_for_programmes: reqMap[s.code].programmes,
      }))
      .sort((a, b) => b.subject_weight - a.subject_weight || a.code.localeCompare(b.code))
  } else {
    // Single group - original behaviour
    const requiredSubjects = subjects.filter(s => s.subject_weight > 0)
    visibleSubjects        = hasChosen ? requiredSubjects : subjects

    if (subjectGate?.kind === 'placeholder_explore') {
      visibleSubjects = [...visibleSubjects].sort((a, b) => {
        const aLocked = !!a.locked
        const bLocked = !!b.locked
        if (aLocked !== bLocked) return aLocked ? 1 : -1
        return a.code.localeCompare(b.code)
      })
    }
  }

  /**
   * Exam components the platform cannot teach, for the programmes currently
   * on screen. Derived from the requirement rows rather than from `subjects`,
   * because /subjects only ever lists subjects with active = true, which is
   * exactly why these were invisible before.
   *
   * Scoped to the selected group when the student holds commitments at more
   * than one, so an Arquitectura candidate does not see another university's
   * Aptidão Física. Empty in 12ª view: the national exam has no such component.
   */
  const untaughtRequirements = (() => {
    if (isNationalView) return []
    const scoped = isMultiGroup && selectedUniversity
      ? realProgrammes.filter(sp => universitiesForSourceInstitution(
          realProgrammes, sourceInstitutionForUniversity(realProgrammes, selectedUniversity),
        ).includes(sp.programme.university))
      : realProgrammes

    const byCode: Record<string, { subject_code: string; subject_name: string; weight: number; programmes: string[] }> = {}
    for (const sp of scoped) {
      for (const r of (sp.programme.requirements ?? [])) {
        if (r.taught !== false) continue
        const name = r.subject_name ?? r.subject_code
        if (!byCode[r.subject_code]) {
          byCode[r.subject_code] = { subject_code: r.subject_code, subject_name: name, weight: r.weight, programmes: [] }
        } else if (r.weight > byCode[r.subject_code].weight) {
          byCode[r.subject_code].weight = r.weight
        }
        const label = sp.programme.name
        if (!byCode[r.subject_code].programmes.includes(label))
          byCode[r.subject_code].programmes.push(label)
      }
    }
    return Object.values(byCode).sort((a, b) => b.weight - a.weight)
  })()

  /**
   * Build the query-param suffix appended to every /questions and /exam
   * navigation so the next page knows which mode + (optionally) which
   * university to query. Always includes `&mode=`; `&university=` only
   * applies in admission view (national_12 isn't institution-scoped per
   * Decision #3).
   */
  const navParams = (): string => {
    const parts: string[] = []
    if (!isNationalView && isMultiGroup && selectedUniversity) {
      parts.push(`university=${encodeURIComponent(selectedUniversity)}`)
    }
    parts.push(`mode=${activeMode}`)
    return `&${parts.join('&')}`
  }

  // Nearest upcoming exam across the subjects actually on screen. Derived
  // rather than fetched, so the strip can never name a date that no card
  // below it shows, and it disappears by construction when the university
  // pill changes to a group with no dates entered yet.
  const nextExam = visibleSubjects.reduce<{ name: string; iso: string; days: number } | null>((best, s) => {
    const days = daysUntilExam(s.exam_date)
    if (days === null || days < 0) return best
    return !best || days < best.days ? { name: s.name, iso: s.exam_date!, days } : best
  }, null)

  // Recommendation: required subject with lowest mastery score (that has been attempted)
  const requiredSubjects = visibleSubjects.filter(s => s.subject_weight > 0)
  const attempted        = requiredSubjects.filter(s => s.mastery_score !== null)
  const miniExamNudgeEligible = visibleSubjects.some(s => (s.questions_seen ?? 0) >= 50)
  const weakest          = attempted.length > 0
    ? attempted.reduce((a, b) => (a.mastery_score! < b.mastery_score! ? a : b))
    : null
  const recommendationVisible =
    showPracticeSupport
    && !isNationalView
    && hasChosen
    && !!weakest
    && weakest.mastery_score !== null
    && weakest.mastery_score < 0.75
  const topDiscoveryBannerVisible =
    showPracticeSupport
    && activeMode === 'admission'
    && !isNationalView
    && !!user?.onboarding_style_v2

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Welcome card after anonymous claim (Slice 11 + 12 close-out,
          2026-05-19). Centered modal sheet - bigger than the previous
          fleeting toast - that names the programme + university the
          student committed to during anonymous onboarding. The "we'll
          help you get into X at Y" framing is the founder's ask:
          students should see their goal reflected back at them the
          moment they sign up.

          Programme + university come from the first non-placeholder
          StudentProgramme (priority order). When there's no
          programme committed yet (rare - anon onboarding asks for
          one, but they may have skipped), the card falls back to a
          generic welcome line that still acknowledges the kept
          questions. */}
      {showClaimWelcome && (() => {
        const primaryProg = studentProgrammes
          .filter(sp => !sp.programme.is_placeholder)
          .sort((a, b) => a.priority - b.priority)[0]
        const programmeName = primaryProg?.programme.name ?? null
        const universityName = primaryProg?.programme.university ?? null
        const questionsKept = showClaimWelcome.questions_kept
        const studentName = (user?.name && user.name !== 'Convidado') ? user.name : null
        return (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Bem-vinda - a tua conta foi criada"
            onClick={() => setShowClaimWelcome(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-2">
                <span className="text-3xl" aria-hidden>👋</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 text-center">
                {studentName ? `Bem-vindo(a), ${studentName}!` : 'Bem-vindo(a) ao Tutor!'}
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed mt-3 text-center">
                Estamos felizes em ter-te aqui.
                {programmeName && universityName ? (
                  <>
                    {' '}Vamos ajudar-te a entrar em{' '}
                    <strong>{programmeName}</strong> na{' '}
                    <strong>{universityName}</strong>.
                  </>
                ) : (
                  <> Vamos ajudar-te a chegar onde queres.</>
                )}
              </p>
              {questionsKept > 0 && (
                <p className="text-xs text-gray-500 leading-relaxed mt-3 text-center">
                  As tuas {questionsKept} {questionsKept === 1 ? 'questão' : 'questões'} e o teu progresso ficaram guardados no teu perfil.
                </p>
              )}
              <button
                onClick={() => setShowClaimWelcome(null)}
                className="mt-5 w-full bg-indigo-600 text-white text-sm font-semibold py-3 rounded-xl hover:bg-indigo-700"
              >
                Vamos lá
              </button>
            </div>
          </div>
        )
      })()}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand/logo.svg" alt="Tutor" className="h-6" />
            <span className="sr-only">Tutor</span>
          </div>
          <div className="flex items-center gap-3">
            {streak > 0 && (
              <button
                onClick={() => navigate('/profile')}
                className={`flex items-center gap-1 text-sm font-semibold ${streakIsProtected ? 'text-blue-600' : 'text-orange-500'}`}
                title={streakIsProtected ? 'Sequência protegida' : 'Sequência actual'}
              >
                {streakIsProtected ? '❄️' : '🔥'} {streak}
              </button>
            )}
            <PortalButton />
            {/* 2026-05-01: replaced the admin-only "Admin" button with the
                shared PortalButton - also surfaces "Portal do Professor"
                for teachers using the student app for QA / review. */}
            <button onClick={() => navigate('/profile')} className="text-sm text-gray-500 hover:text-gray-700">{visibleFirstName(user?.name)}</button>
            <button onClick={logout} className="text-xs text-gray-400 hover:text-gray-600">Sair</button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* V2 active-context switcher - visible to v2 students with ≥1 commitment.
            Hidden in 12ª view (2026-04-28): programme commitments are an
            admission concept (Decision: a 12ª student who wants to add/edit
            programmes toggles to admission mode first). The 12ª view stays
            content-only - the top toggle is the primary context. */}
        {user?.onboarding_style_v2 && studentProgrammes.length > 0 && !isNationalView && (
          <div className="mb-4">
            <ActiveContextSwitcher
              programmes={studentProgrammes.map<SwitcherProgramme>(sp => ({
                id:       sp.id,
                priority: sp.priority,
                period:   sp.period,
                programme: {
                  id:                     sp.programme.id,
                  name:                   sp.programme.name,
                  university:             sp.programme.university,
                  university_max_options: sp.programme.university_max_options,
                  is_placeholder:         sp.programme.is_placeholder ?? false,
                  periods_available:      sp.programme.periods_available,
                },
              }))}
              activeProgrammeId={user.active_programme_id ?? null}
              maxProgrammes={user.max_programmes}
              maxUniversities={user.max_universities}
              onChanged={reloadAll}
            />
          </div>
        )}

        {/* Greeting (Voice Foundation PR, 2026-05-03). Time-of-day
            variant by default; streak-first headline at ≥7 days. See
            greetingFor() above + docs/voice-and-tone.md.
            2026-05-24 (S2+S3): wrapped with a subtle brand-blue
            left-accent so the hero reads as the page anchor (the rest
            of the page is grid + tools, no other h1 above the
            fold), and surfaced the streak as a calm warm-amber chip
            inline with the headline when current_days > 0. Chip uses
            the same shy palette as the band chip on QuestionCard. */}
        <div className="mb-6 pl-3 sm:pl-4 border-l-4 border-blue-500/30">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-gray-900">
              {user?.name ? greetingFor(user.name, maputoHour(), streak, user.gender) : ''}
            </h1>
            {streak > 0 && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${streakIsProtected ? 'text-blue-700 bg-blue-50 border border-blue-200' : 'text-amber-700 bg-amber-50 border border-amber-200'}`}
                aria-label={`${streakIsProtected ? 'Sequência protegida' : 'Sequência'} de ${streak} ${streak === 1 ? 'dia' : 'dias'}`}
              >
                <span aria-hidden>{streakIsProtected ? '❄️' : '🔥'}</span>
                {streak} {streak === 1 ? 'dia' : 'dias'}
              </span>
            )}
          </div>
          <p className="text-gray-500 mt-1 text-sm">
            {/* 2026-05-01: copy split by mode. National 12ª students don't
                pick courses - they study for the national exam, full stop.
                The course-centric copy below only applies when on admission
                view AND the student has committed to a programme. */}
            {isNationalView
              ? `A estudar para ${visibleSubjects.length} disciplina${visibleSubjects.length !== 1 ? 's' : ''} - Exame Nacional 12ª Classe`
              : hasChosen
                ? `${visibleSubjects.length} disciplina${visibleSubjects.length !== 1 ? 's' : ''} para o(s) curso(s) seleccionado(s)`
                : 'Escolhe uma matéria para praticar'}
          </p>
        </div>

        {/* Próximo exame (2026-08-17). Nearest upcoming date among the
            subjects on screen. Renders nothing at all when no visible
            subject has a future date, which covers a group whose edital
            hasn't been entered, a group whose exams are done, and the
            anonymous student with no commitments. Sits above the mode
            tabs deliberately: the exam date does not change between
            practising and simulating. */}
        {nextExam && (
          <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2.5">
            <span className="text-base shrink-0" aria-hidden>📅</span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-indigo-500 truncate">Próximo exame · {nextExam.name}</p>
              <p className="text-sm font-semibold text-indigo-900">{examCountdownSentence(nextExam.iso)}</p>
            </div>
            <span className="shrink-0 text-[11px] font-medium text-indigo-700 bg-white border border-indigo-100 rounded-full px-2.5 py-1">
              {formatExamDay(nextExam.iso)}
            </span>
          </div>
        )}

        {/* Guidance strip - UI_UX_IMPLEMENTATION_PLAN_MAY_2026.md
            Slice 1 (2026-05-12). Replaces the dominant DailyMissionCard
            hero with a slim, single-line suggestion surface above the
            subject grid. Priority-locked four-case matrix: active
            mission → mission done → recent activity → fresh student.
            Self-hides on offline mission-fetch failure (falls through
            to the recent / fresh branches using local subjects state).
            DailyMissionCard import retained for rollback.

            Principle: launcher, not controller. The strip suggests;
            the subject grid below remains the page's main destination. */}
        <GuidanceStrip
          mode={activeMode}
          activeSubject={user?.active_subject ?? 'MAT'}
          subjects={subjects.map(s => ({
            code:            s.code,
            name:            s.name,
            mastery_score:   s.mastery_score,
            subject_weight:  s.subject_weight ?? 0,
          }))}
          navSuffix={navParams()}
          onStateChange={setGuidanceState}
        />

        {/* Profile-completion banner promoted back above the subject grid.
            Founder feedback: phone + province matter for stats / ranking
            integrity, so this prompt should stay near the top of the page
            instead of living below the catalogue. Keep it below the
            guidance strip so the study suggestion still anchors first. */}
        {(() => {
          if (!user) return null
          if (user.is_anonymous) return null
          const missing = missingProfileFields(user)
          if (missing.length === 0) return null
          if (isProfileBannerDismissed(profileBannerDismissDays)) return null
          return (
            <div className="mb-5">
              <ProfileCompletionBanner
                missingFields={missing}
                dismissDays={profileBannerDismissDays}
              />
            </div>
          )
        })()}

        {/* "Esta semana" directional capsule (PR 3.1, 2026-05-06).
            Moved to the top section per founder feedback so it can act
            as a quick coaching moment before the student chooses a
            subject. Practice-only, admission-only, and auto-dismisses
            after 10s so it informs without occupying the launchpad for
            too long. */}
        {weeklyRecapVisible && (
          <div className="mb-5 rounded-xl border border-gray-100 bg-white px-5 py-4 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Esta semana
            </p>
            <div className="space-y-1.5">
              {weeklyImproved.slice(0, 1).map(s => (
                <p key={`up-${s.subject_code}`} className="text-sm text-gray-700 leading-snug">
                  <span className="text-green-600 font-semibold">↑</span> Subiste{' '}
                  <span className="font-semibold">{Math.round(s.delta * 100)}%</span> em{' '}
                  <span className="font-semibold">{s.name}</span>.
                </p>
              ))}
              {weeklyRisky.slice(0, 1).map(s => (
                <p key={`down-${s.subject_code}`} className="text-sm text-gray-700 leading-snug">
                  <span className="text-amber-600 font-semibold">⚠</span>{' '}
                  <span className="font-semibold">{s.name}</span> caiu{' '}
                  <span className="font-semibold">{Math.abs(Math.round(s.delta * 100))}%</span> - fica atento.
                </p>
              ))}
              {weeklyNextAction && (
                <button
                  onClick={() => continueToPractice(`/questions?subject=${weeklyNextAction.subject_code}${navParams()}`)}
                  className="text-sm text-indigo-700 hover:text-indigo-900 font-semibold leading-snug text-left mt-1"
                >
                  → {weeklyNextAction.copy}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Admission-mode reinforcement recommendation. Keep it in the
            top coaching stack so students see the weakest required area
            before making a study choice. */}
        {recommendationVisible && weakest && (
          <button
            onClick={() => continueToPractice(`/questions?subject=${weakest.code}${navParams()}`)}
            className="w-full mb-5 text-left bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  A área onde precisas de mais reforço é{' '}
                  <span className="font-black">
                    {weakest.name}{weakest.exam_variant ? ` · Prova ${weakest.exam_variant}` : ''}
                  </span>.
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Vamos lá investir aqui hoje. Treino de reforço começa abaixo.
                </p>
              </div>
              <span className="text-amber-500 text-lg shrink-0">→</span>
            </div>
          </button>
        )}

        {/* 12ª opt-in nudge promoted to the top stack. Founder feedback:
            ask about national-exam prep before the subject grid, not
            after the student has already scanned the catalogue. */}
        {showPracticeSupport && user?.preferred_mode === 'admission' && user?.is_current_grade_12 !== true && (() => {
          const KEY = 'national_12_banner_dismissed_until'
          const dismissedUntil = Number(localStorage.getItem(KEY) ?? '0')
          if (dismissedUntil > Date.now()) return null
          const dismiss = () => {
            try { localStorage.setItem(KEY, String(Date.now() + grade12OptInDismissDays * 86_400_000)) } catch {}
            setSubjectsReloadKey(k => k + 1)
          }
          const enableBoth = async () => {
            try {
              try { sessionStorage.setItem(`grade12_opt_in_pending_${user!.id}`, '1') } catch {}
              await api.post('/auth/profile-contact', { preferred_mode: 'both' })
              await refreshAuthIfStale(true)
              setActiveMode('national_12')
              if (subjectGate?.needs_initial_pick) {
                setPickerOpen(true)
              }
            } catch (err) {
              console.warn('[subjects] failed to enable national_12 mode', err)
            }
          }
          return (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <span className="text-2xl shrink-0 mt-0.5">🎓</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-amber-900 leading-snug">
                  Queres preparar ou rever questões dos exames da 12.ª classe também?{' '}
                  <button
                    onClick={enableBoth}
                    className="font-semibold underline hover:text-amber-700"
                  >
                    Clica aqui
                  </button>
                  {' '}ou adiciona no teu{' '}
                  <button
                    onClick={() => navigate('/profile')}
                    className="font-semibold underline hover:text-amber-700"
                  >
                    perfil →
                  </button>
                </p>
              </div>
              <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="text-amber-400 hover:text-amber-600 text-lg leading-none shrink-0"
              >
                ✕
              </button>
            </div>
          )
        })()}

        {/* Top priority slot - keep only the truly important account-level
            blocker near first paint. Softer nudges (notifications, name
            cleanup, 12ª opt-in) live below the subject grid so students
            can choose what to study first. */}
        {(() => {
          // Email verification (Tier-1). Soft amber banner with a
          // resend CTA + admin-configurable dismiss window. Never blocks usage; the
          // student's actual signup already succeeded. Hidden in
          // DEMO_MODE (no Firebase user) and once Firebase reports
          // emailVerified=true. Future Tier-2 work gates WhatsApp /
          // subscription / parent-link behind this same flag.
          const showEmailVerify =
            IS_FIREBASE_ENABLED
            && !!auth?.currentUser
            && !emailVerified
            && emailVerifyDismissedUntil <= Date.now()
            && !!user?.id
          if (showEmailVerify) {
            const dismissForToday = () => {
              const until = Date.now() + emailVerifyDismissHours * 60 * 60 * 1000
              try { localStorage.setItem(`email_verify_dismissed_until_${user!.id}`, String(until)) } catch {}
              setEmailVerifyDismissedUntil(until)
            }
            const resend = async () => {
              if (!auth?.currentUser || emailVerifyResend === 'sending') return
              setEmailVerifyResend('sending')
              try {
                await sendEmailVerification(auth.currentUser)
                setEmailVerifyResend('sent')
              } catch {
                setEmailVerifyResend('error')
              }
            }
            const refreshStatus = async () => {
              if (!auth?.currentUser) return
              try {
                await auth.currentUser.reload()
                setEmailVerified(auth.currentUser.emailVerified)
              } catch { /* non-fatal */ }
            }
            return (
              <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <div className="flex items-start gap-3">
                  <span className="text-2xl shrink-0 mt-0.5">✉️</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900 leading-snug">Confirma o teu email</p>
                    <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                      Enviámos um link para <strong className="break-all">{user?.email}</strong>. Clica no link para protegeres a tua conta. Já confirmaste?{' '}
                      <button
                        onClick={refreshStatus}
                        className="underline font-semibold text-amber-900 hover:text-amber-700"
                      >
                        Verificar agora
                      </button>
                    </p>
                    {emailVerifyResend === 'sent' && (
                      <p className="text-[11px] text-green-700 mt-2">Email reenviado - verifica a tua caixa de entrada e spam.</p>
                    )}
                    {emailVerifyResend === 'error' && (
                      <p className="text-[11px] text-red-700 mt-2">Não foi possível reenviar agora. Tenta novamente daqui a alguns minutos.</p>
                    )}
                  </div>
                  <button
                    onClick={dismissForToday}
                    aria-label="Dispensar por hoje"
                    className="text-amber-400 hover:text-amber-600 text-lg leading-none shrink-0"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3 ml-9">
                  <button
                    onClick={resend}
                    disabled={emailVerifyResend === 'sending' || emailVerifyResend === 'sent'}
                    className="text-xs bg-amber-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-40"
                  >
                    {emailVerifyResend === 'sending' ? 'A enviar…' : emailVerifyResend === 'sent' ? 'Enviado ✓' : 'Reenviar email'}
                  </button>
                </div>
              </div>
            )
          }
          return null
        })()}

        {/* Name-cleanup nudge - promoted into the top stack so an
            auto-derived student name is easier to correct without
            hiding the study launcher itself. Still practice-only. */}
        {showPracticeSupport && (() => {
          if (!user?.name || !user?.email) return null
          const emailPrefix = user.email.split('@')[0].toLowerCase()
          const looksAutoDerived =
            user.name === 'Estudante' || user.name.toLowerCase() === emailPrefix
          if (!looksAutoDerived) return null
          const KEY = 'name_nudge_dismissed_until'
          const dismissedUntil = Number(localStorage.getItem(KEY) ?? '0')
          if (dismissedUntil > Date.now()) return null
          const dismiss = () => {
            try { localStorage.setItem(KEY, String(Date.now() + nameFixDismissDays * 86_400_000)) } catch {}
            setSubjectsReloadKey(k => k + 1)
          }
          return (
            <div className="mb-5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <span className="text-2xl shrink-0 mt-0.5">👋</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-blue-900 leading-snug">
                  Olá! O teu nome ficou como <strong>{user.name}</strong>. Queres mudar?{' '}
                  <button onClick={() => navigate('/profile')} className="font-semibold underline hover:text-blue-700">
                    Actualizar no perfil →
                  </button>
                </p>
              </div>
              <button onClick={dismiss} aria-label="Dispensar" className="text-blue-400 hover:text-blue-600 text-lg leading-none shrink-0">
                ✕
              </button>
            </div>
          )
        })()}

        {/* Mode toggle - only shown for 'both' students. Single-mode
            students change their mode via /profile, not here (DUAL_MODE
            plan §3.1). 12ª first (default landing per Q1); admission
            second. Word-only labels - emoji-free per the 2026-04-27
            polish pass. */}
        {/* In-page mode toggle removed in the bottom-nav PR (2026-05-02).
            Mode now lives as separate tabs in <StudentTabBar>. The
            tab bar reads + writes ?mode= on /subjects, and this page
            picks it up via the URL-sync useEffect above. */}

        {/* University filter pills - only shown when student spans multiple
            exam groups in admission view. National_12 isn't institution-
            scoped (Decision #3) so the pill row stays hidden in that view. */}
        {isMultiGroup && !isNationalView && (
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span className="text-xs text-gray-400 font-medium shrink-0">A preparar para:</span>
            {studentUnis.map(uni => (
              <button
                key={uni}
                onClick={() => {
                  setSelectedUniversity(uni)
                  try { sessionStorage.setItem(PILL_STORAGE_KEY, uni) } catch {}
                }}
                className={`text-sm font-semibold px-4 py-1.5 rounded-full border transition-all ${
                  selectedUniversity === uni
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                }`}
              >
                {uni}
              </button>
            ))}
          </div>
        )}

        {/* Push-permission banner. Mounted 2026-08-15.
            It was written on 2026-04-28 to replace the static card buried at
            /profile → Notificações, and was then never rendered anywhere: the
            replacement shipped, the thing it replaced stayed, and the only
            path to the permission prompt remained a settings sub-page.
            Result on demo: 204 students, 48 active in 30 days, and the
            PushSubscription table completely empty. Nobody had ever been
            asked.

            It self-suppresses unless getPushState() === 'default', and caps
            itself at 2 dismissals a day, so it is a recurring nudge rather
            than a nag.

            Hidden for anonymous students on purpose. POST /push/subscribe is
            blocked for them (asserted in smoke-anonymous-route-policy), so
            they would grant permission, have the subscribe call 403, store
            nothing, and never see the banner again because the permission is
            no longer 'default'. Browser permission is one-shot: spending it
            on a request that cannot succeed costs them the channel for good. */}
        {!user?.is_anonymous && <NotificationsBanner />}

        {/* Discovery banner - top-only in admission. Keep it out of the
            below-grid support area so students don't see the same
            reevaluate/discovery nudge duplicated in different positions
            depending on their programme state. */}
        {topDiscoveryBannerVisible && (
          <SubjectsBanners
            student={user}
            onChanged={reloadAll}
            showWhatsappBanner={false}
            showReevaluateBanner={true}
            showGrade12Reprompt={false}
          />
        )}

        {/* Programme chips - keep the selected 1ª / 2ª options right
            below the active preparation context in admission mode. */}
        {showPracticeSupport && !isNationalView && hasChosen && studentProgrammes.length > 0 && (() => {
          const filtered = sortProgrammesByUniversityThenPriority(
            selectedUniversity
              ? studentProgrammes.filter(sp => sp.programme.university === selectedUniversity)
              : studentProgrammes
          )
          return (
            <div className="mb-5">
              {programmeListExpanded || filtered.length <= 2 ? (
                <div className="flex flex-wrap gap-2">
                  {filtered.map(sp => (
                    <button
                      key={sp.id}
                      onClick={() => setDetailsProgId(sp.programme.id)}
                      className="flex items-center gap-1.5 text-xs bg-indigo-100 text-indigo-800 font-semibold px-3 py-1.5 rounded-full hover:bg-indigo-200 transition-colors"
                    >
                      <span className="text-indigo-400">{sp.priority}ª</span>
                      {sp.programme.university} · {sp.programme.name}
                      <span className="text-indigo-400 text-[10px] ml-0.5">ⓘ</span>
                    </button>
                  ))}
                  {filtered.length > 2 && (
                    <button
                      onClick={() => setProgrammeListExpanded(false)}
                      className="text-xs text-gray-400 hover:text-indigo-600 px-2 py-1.5 rounded-full hover:bg-indigo-50 transition-colors"
                    >
                      recolher
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (user?.onboarding_style_v2) {
                        const uniqueUnis = [...new Set(filtered.map(sp => sp.programme.university))]
                        const targetUni  = selectedUniversity ?? (uniqueUnis.length === 1 ? uniqueUnis[0] : null)
                        if (targetUni) {
                          navigate(`/courses/${encodeURIComponent(targetUni)}`)
                        } else {
                          navigate('/universities')
                        }
                      } else {
                        navigate('/onboarding')
                      }
                    }}
                    className="text-xs text-gray-400 hover:text-indigo-600 px-2 py-1.5 rounded-full hover:bg-indigo-50 transition-colors"
                  >
                    alterar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setProgrammeListExpanded(true)}
                  className="flex items-center gap-2 text-xs bg-indigo-50 text-indigo-700 font-semibold px-3 py-2 rounded-full hover:bg-indigo-100 transition-colors"
                >
                  <span>{filtered.length} cursos seleccionados</span>
                  <span className="text-indigo-400">ver</span>
                </button>
              )}
            </div>
          )
        })()}

        {/* Study mode toggle.
            2026-05-01: "Exame Completo" is now available in 12ª view too -
            INEE-12 ExamConfigs exist for all 9 subjects and the
            /exam-sessions/start route honours the INEE_GROUP override
            for national_12 mode. Pre-fix the button was hidden in 12ª
            because no INEE config existed yet; that's no longer true. */}
        <div className="flex gap-1 mb-3 bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setStudyMode('practice')}
            className={`flex-1 px-2 py-3 rounded-lg text-sm font-semibold transition-all ${
              studyMode === 'practice'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Prática
          </button>
          {!user?.is_anonymous && (
            <>
              <button
                onClick={() => setStudyMode('mini')}
                className={`flex-1 px-2 py-3 rounded-lg text-sm font-semibold transition-all ${
                  studyMode === 'mini'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Mini Exame
              </button>
              <button
                onClick={() => setStudyMode('exam')}
                className={`flex-1 px-2 py-3 rounded-lg text-sm font-semibold transition-all ${
                  studyMode === 'exam'
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Exame Completo
              </button>
            </>
          )}
        </div>

        {/* Quota state line - shown below the toggle when on Mini or Simulação.
            Copy is tier-aware and uses PT-Moçambique (pre-AO90). */}
        {quota && studyMode === 'mini' && (
          <p className="mb-5 text-xs text-gray-500">
            {quota.mini.limit_per_week === null
              ? 'Mini Exames ilimitados nesta conta.'
              : miniAllowed
                ? `Mini Exame desta semana: ${quota.mini.used_this_week}/${quota.mini.limit_per_week} usado.`
                : quota.mini.next_available_at
                  ? `Já usaste o teu Mini Exame desta semana. Volta em ${formatCooldown(quota.mini.next_available_at)}.`
                  : 'Limite de Mini Exame atingido. Desbloqueia Premium para fazer mais.'}
          </p>
        )}
        {quota && studyMode === 'exam' && (
          <p className="mb-5 text-xs text-gray-500">
            {quota.full.locked_forever
              ? `Exame Completo gratuito já usado. Ranking actual - desbloqueia Premium para refazer.`
              : quota.full.lifetime_limit === 1 && quota.full.lifetime_completed === 0
                ? 'Tens um único Exame Completo gratuito. Depois deste, o teu ranking fica fixo até upgrade.'
                : quota.full.month_limit !== null
                  ? `Exames Completos este mês: ${quota.full.month_completed}/${quota.full.month_limit}.`
                  : 'Exames Completos ilimitados nesta conta.'}
          </p>
        )}

        {/* Subject cards. id="subject-grid" anchors the hero mission
            card's scroll-down CTA when the mission has no specific
            destination ("Escolher matéria ↓" → smooth-scrolls here). */}
        <div id="subject-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visibleSubjects.map((s) => {
            const c          = CLS[COLOR[s.code] ?? 'blue']
            const isRequired = s.subject_weight > 0
            const mastery    = s.mastery_score
            const masteryPct = mastery !== null ? Math.round(mastery * 100) : null
            const mLabel     = mastery !== null ? masteryLabel(mastery) : null
            const isLocked   = s.requires_subscription && !isSubscribed
            // Free-tier subject gate (2026-04-28). Independent from
            // `isLocked` - a subject can be free-locked (student didn't
            // pick) AND/OR subscription-locked (paid feature). Free-lock
            // wins for the click handler since swapping is the cheaper
            // unlock path.
            const freeLocked = !!s.locked

            // Programme names without university prefix
            const progNames = s.required_for_programmes
              .map(p => p.split(' · ').slice(1).join(' · '))
              .join(' · ')

            // Per-programme breakdown filtered to the active pill.
            // The top-right pill always carries the weight info,
            // collapsed when uniform and concatenated with " | " when
            // commits diverge - order matches priority (1ª opção,
            // 2ª opção) so segments line up with the programme names
            // shown below the subject. Examples:
            //   ISCAM, both 60%, no variant   → "60% admissão"
            //   UEM, Eng. Eléctrica Var I 50% +
            //        Estatística Var II 70%   → "Prova I · 50% | Prova II · 70%"
            const breakdownAll = s.subject_breakdown ?? []
            const breakdownVisible = selectedUniversity
              ? breakdownAll.filter(b => b.university_short_name === selectedUniversity)
              : breakdownAll
            // Distinct (weight, variant) segments preserving first-seen
            // priority order. Two programmes with the same numbers
            // collapse into one segment.
            const seenSegKeys = new Set<string>()
            const pillSegments: Array<{ variant: string; weight: number }> = []
            for (const b of breakdownVisible) {
              const key = `${b.weight}|${b.variant ?? ''}`
              if (seenSegKeys.has(key)) continue
              seenSegKeys.add(key)
              pillSegments.push({ variant: b.variant, weight: b.weight })
            }
            // Pill text - uniform "Admissão: ..." prefix across all
            // universities for consistency. Each segment is
            // "Prova X · Y%" when the uni has variants (UEM) or just
            // "Y%" when it doesn't (ISCAM, UJC, UP, ...). Multiple
            // distinct segments join with " | " in priority order so
            // they line up with the programme names below.
            const segText = pillSegments.map(s =>
              s.variant ? `Prova ${s.variant} · ${s.weight}%` : `${s.weight}%`,
            )
            const pillText = pillSegments.length === 0
              ? null
              : `Admissão: ${segText.join(' | ')}`

            // "Yours" ring (2026-05-24, revised). Both modes now derive
            // from the LIVE active-programme requirements set — the
            // student's currently-committed programmes' subject codes.
            // Pre-fix the 12ª branch used school_subject_codes (the
            // Layer A chip picker) which stayed sticky after the
            // student uncommitted from a programme. Now the ring
            // tracks commits live: uncommit a programme → the rings
            // for its previously-required-only subjects disappear on
            // the next render. Admission keeps using subject_weight
            // (already programme-derived) for symmetry; the
            // visibleSubjects build for 12ª sets subject_weight=0
            // explicitly, hence the separate national branch below.
            const isMineForRing = isNationalView
              ? activeRequiredSubjects.has(s.code)
              : isRequired && hasChosen
            return (
              <div
                key={s.id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all ${
                  isMineForRing
                    ? `border-gray-200 ring-2 ${c.ring}`
                    : 'border-gray-100'
                } ${isLocked || freeLocked ? 'opacity-70' : ''}`}
              >
                <div className={`${c.hdr} px-5 pt-5 pb-4`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-3xl">{EMOJI[s.code] ?? '📚'}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {freeLocked && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
                          🔒 Bloqueada
                        </span>
                      )}
                      {isLocked && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                          🔒 Premium
                        </span>
                      )}
                      {/* 12ª badge - small, unobtrusive (§3.8). */}
                      {isNationalView ? (
                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                          12ª
                        </span>
                      ) : (
                        // Admission weight pill (PR follow-up 2026-05-05).
                        // Single segment when commits are uniform; two+
                        // segments joined by " | " when commits diverge,
                        // preserving priority order so segments visually
                        // line up with the programme names rendered
                        // below.
                        pillText && (
                          <span className="text-xs font-semibold text-indigo-700 bg-white border border-indigo-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {pillText}
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  {/* Subject name. The "· Prova X" suffix that used to
                      sit here showed only one variant - also misleading
                      for multi-variant commits. Per-programme rows
                      below carry that information accurately. */}
                  <h2 className="text-lg font-semibold text-gray-900 mt-2">
                    {s.name}
                  </h2>
                  {/* Subject.description used to render here ("Matemática do
                      ensino secundário para acesso à universidade", "... para o
                      exame nacional da 12ª Classe"). Removed 2026-10-21: it
                      restates the subject name and the mode the student already
                      chose, so it cost a line on every card at 360px and told
                      them nothing. The field is still carried by /subjects and
                      still used by FreeSubjectPickerModal, where a description
                      genuinely helps someone choosing which subjects to unlock. */}

                  {/* Programme names below the subject - priority
                      order, joined with " · ". Weights/variants live
                      in the top-right pill (uniform: one segment;
                      mixed: " | "-joined segments) so the names line
                      up positionally with the pill segments. Filtered
                      to the active uni pill. Falls back to the legacy
                      progNames join when the breakdown is missing
                      (offline cache, older client). */}
                  {isRequired && hasChosen && !isNationalView && (
                    breakdownVisible.length > 0 ? (
                      <p className="text-xs text-indigo-500 mt-1 font-medium truncate">
                        {breakdownVisible.map(b => b.programme_name).join(' · ')}
                      </p>
                    ) : (
                      progNames && (
                        <p className="text-xs text-indigo-500 mt-1 font-medium truncate">{progNames}</p>
                      )
                    )
                  )}

                  {/* Exam date. Shown on every card that has one, including
                      the subject already named in the strip above: the rule
                      "a dated subject shows its date" has no exceptions, so
                      a student scrolling down never has to scroll back up.
                      examCountdownLabel returns null for a missing or past
                      date and the line vanishes entirely - there is no
                      empty state, because "no date" is not news. */}
                  {examCountdownLabel(s.exam_date) && (
                    <p className="text-[11px] text-indigo-600 mt-1.5 flex items-center gap-1">
                      <span aria-hidden>📅</span>
                      {examCountdownLabel(s.exam_date)}
                    </p>
                  )}
                </div>

                {/* Mastery bar - practice mode only.
                    2026-05-02: dropped the bare BKT % from the right-side
                    label. The number was being read as "% correct out of
                    attempted" by students, but it's actually a Bayesian
                    Knowledge Tracing estimate (services/mastery.ts) - slow
                    correct gives less credit, difficulty matters, etc.
                    The qualitative label (Por começar / Em progresso /
                    A melhorar / Dominado) plus the progress bar below
                    convey the signal honestly without the misleading
                    digits. The progress bar still reads from masteryPct
                    so the visual proportion is preserved. */}
                {studyMode === 'practice' && mastery !== null && (
                  <div className="px-5 py-2 border-b border-gray-50 bg-white">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <span className="text-xs text-gray-400">Domínio</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {/* Week-over-week delta chip (PR 3.1, 2026-05-06).
                            Threshold ±2pp filters out attempt-level noise
                            so the chip only fires on a real directional
                            move. Hidden when no topic in this subject was
                            practiced this week (delta = null). */}
                        {s.mastery_delta_7d != null && Math.abs(s.mastery_delta_7d) >= 0.02 && (
                          <span
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              s.mastery_delta_7d > 0
                                ? 'text-green-700 bg-green-50 border border-green-100'
                                : 'text-red-700 bg-red-50 border border-red-100'
                            }`}
                            title="Variação nos últimos 7 dias"
                          >
                            {s.mastery_delta_7d > 0 ? '↑' : '↓'} {Math.abs(Math.round(s.mastery_delta_7d * 100))}%
                          </span>
                        )}
                        <span className={`text-xs font-semibold ${mLabel!.color}`}>
                          {mLabel!.text}
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${c.bar}`}
                        style={{ width: `${masteryPct}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Topics - practice mode only (drill mode).
                    Show the top 4 topics by question_count on the card by
                    default; the rest hide behind "Ver mais". Keeps the card
                    compact for subjects with 15+ tagged topics while still
                    letting the student drill into any of them. */}
                {studyMode === 'practice' && (() => {
                  const TOP_TOPICS_LIMIT = 4
                  // Sort by "fresh" first — questions the student hasn't
                  // attempted yet. Ties break on raw question_count so a
                  // bigger pool wins when two topics have the same
                  // freshness. A topic saturates as the student practises
                  // it and drops down the list, letting less-touched
                  // topics rotate up.
                  const sortedTopics = [...s.topics].sort((a, b) => {
                    const aFresh = (a.question_count ?? 0) - (a.student_attempts_count ?? 0)
                    const bFresh = (b.question_count ?? 0) - (b.student_attempts_count ?? 0)
                    if (bFresh !== aFresh) return bFresh - aFresh
                    return (b.question_count ?? 0) - (a.question_count ?? 0)
                  })
                  const isExpanded = expandedTopicSubjects.has(s.code)
                  const visibleTopics = isExpanded
                    ? sortedTopics
                    : sortedTopics.slice(0, TOP_TOPICS_LIMIT)
                  const hiddenCount = sortedTopics.length - visibleTopics.length
                  const toggleExpanded = () => {
                    setExpandedTopicSubjects((prev) => {
                      const next = new Set(prev)
                      if (next.has(s.code)) next.delete(s.code)
                      else next.add(s.code)
                      return next
                    })
                  }
                  return (
                    <div className="px-5 py-3 border-b border-gray-50">
                      <div className="flex flex-wrap gap-1.5">
                        {visibleTopics.map((t) => (
                          <button
                            key={t.code}
                            onClick={() => isLocked
                              ? (setLockedDetail(null), setLockedSubject(s))
                              : continueToPractice(`/questions?subject=${s.code}&topic=${t.id}&topicName=${encodeURIComponent(t.name)}${navParams()}`)
                            }
                            className={`text-xs px-3 py-2 rounded-full font-medium transition-opacity hover:opacity-70 ${c.badge} ${isLocked ? 'cursor-not-allowed' : ''}`}
                            title={isLocked ? 'Disponível nos planos pagos' : `Praticar apenas ${t.name}`}
                          >
                            {t.name}
                          </button>
                        ))}
                        {(hiddenCount > 0 || isExpanded) && (
                          <button
                            onClick={toggleExpanded}
                            className={`text-xs px-3 py-2 rounded-full font-semibold bg-white ring-2 ring-inset transition-colors ${c.outlined}`}
                            title={isExpanded ? 'Recolher tópicos' : `Mostrar mais ${hiddenCount} tópicos`}
                          >
                            {isExpanded ? 'Ver menos' : `Ver mais (${hiddenCount})`}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-300 mt-1.5">
                        {isLocked ? '🔒 Disponível nos planos pagos' : 'Toca num tópico para praticar só esse'}
                      </p>
                    </div>
                  )
                })()}

                <div className="px-5 py-4">
                  {studyMode === 'practice' ? (
                    <button
                      onClick={() => {
                        // Free-tier gate first - the swap path is cheaper
                        // than a subscription, so prefer it when the
                        // student has both locks active. Behaviour:
                        //   cooldown elapsed → open picker pre-loaded
                        //                      with the current 3 + this
                        //                      one as a hint.
                        //   cooldown active  → alert with next-swap date.
                        if (freeLocked) {
                          if (subjectGate?.can_swap_now) setPickerOpen(true)
                          else if (subjectGate?.next_swap_at) {
                            // Was a dismissible toast that dead-ended. The
                            // student is blocked either way, so offer the paid
                            // route while keeping the free swap date visible.
                            const next = new Date(subjectGate.next_swap_at).toLocaleDateString('pt-MZ')
                            setLockedDetail(`Podes trocar a partir de ${next}.`)
                            setLockedSubject(s)
                          } else {
                            showToast(gateLockedToast, 'warning')
                          }
                          return
                        }
                        if (isLocked) { setLockedDetail(null); setLockedSubject(s); return }
                        continueToPractice(`/questions?subject=${s.code}${navParams()}`)
                      }}
                      className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${
                        (isLocked || freeLocked) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : `text-white ${c.btn}`
                      }`}
                    >
                      {freeLocked
                        ? gateLockedActionLabel
                        : isLocked
                          ? '🔒 Desbloquear com um plano pago'
                          : mastery !== null && mastery < 0.4
                            ? 'Praticar - precisa de atenção'
                            : 'Praticar'}
                    </button>
                  ) : studyMode === 'mini' ? (
                    <button
                      onClick={() => {
                        if (freeLocked) {
                          if (subjectGate?.can_swap_now) setPickerOpen(true)
                          else showToast(gateLockedToast, 'warning')
                          return
                        }
                        if (isLocked) { setLockedDetail(null); setLockedSubject(s); return }
                        if (!miniAllowed) { setMiniLockedSubject(s); return }
                        setMiniSubject(s)
                      }}
                      // Grey signals unavailable, but every one of these states
                      // now opens something that explains why, so the cursor
                      // must not claim the button is inert.
                      className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${
                        isLocked || freeLocked || !miniAllowed
                          ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {freeLocked
                        ? gateLockedActionLabel
                        : isLocked
                          ? '🔒 Desbloquear com um plano pago'
                          : !miniAllowed
                            ? 'Mini Exame desta semana usado'
                            : 'Mini Exame'}
                    </button>
                  ) : examConfigMap[s.code] ? (
                    <button
                      onClick={() => {
                        if (isLocked) { setLockedDetail(null); setLockedSubject(s); return }
                        // Lock-out guard: free tier with full-exam lifetime cap already used.
                        // Contract §4.1 locked_forever. Shows a modal explaining they're
                        // already on the ranking and Premium unlocks re-attempts.
                        if (!fullAllowed && quota?.full.locked_forever) { setFullLockedSubject(s); return }
                        if (!fullAllowed) { setFullLockedSubject(s); return } // monthly cap too
                        trackAnalytics('full_session_started', {
                          subject_code: s.code,
                          university:   isMultiGroup && selectedUniversity ? selectedUniversity : null,
                        })
                        navigate(`/exam?subject=${s.code}${navParams()}`)
                      }}
                      disabled={!isLocked && !fullAllowed && !(quota?.full.locked_forever) && false /* allow click to show modal */}
                      className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${
                        isLocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : !fullAllowed ? 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {isLocked ? 'Desbloquear com um plano pago' : !fullAllowed ? 'Exame Completo bloqueado' : (
                        <span className="flex flex-col items-center justify-center">
                          <span>Exame Completo</span>
                          <span className="text-xs opacity-80">{examConfigMap[s.code].duration_minutes}min · {examConfigMap[s.code].question_count} questões</span>
                        </span>
                      )}
                    </button>
                  ) : (
                    <button disabled className="w-full py-3 rounded-xl text-sm font-semibold bg-gray-100 text-gray-400 cursor-not-allowed">
                      Disponível em breve
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          {/* Exam components the platform does not teach (Desenho, Aptidão
              Física, Aptidão em Desenho, Inquérito Sociolinguístico). They
              carry real weight in the edital, so leaving them out made the
              card grid read as the whole exam when it was only part of it:
              an Arquitectura candidate saw "Matemática · 30%" and nothing
              accounting for the other 70%. Rendered in the same grid, in
              weight order alongside the taught subjects, but inert: no
              topics, no mastery, no practice button. */}
          {untaughtRequirements.map(r => (
            <div
              key={`nd-${r.subject_code}`}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden"
            >
              <div className="p-5 bg-gray-50">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className="text-3xl grayscale opacity-60" aria-hidden>📄</span>
                  <span className="text-xs font-semibold text-gray-500 border border-gray-300 rounded-full px-2.5 py-1 whitespace-nowrap">
                    Admissão: {r.weight}%
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-500">{r.subject_name}</h3>
                {r.programmes.length > 0 && (
                  <p className="text-sm text-indigo-400 mt-1">{r.programmes.join(' · ')}</p>
                )}
              </div>
              <div className="p-5">
                <p className="text-sm font-semibold text-gray-500 leading-snug text-center">
                  Disciplina não disponível no Tutor
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* If non-required subjects exist, show them collapsed */}
        {!hasChosen && subjects.filter(s => s.subject_weight === 0).length > 0 && (
          <div className="mt-6">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-3">
              Outras matérias disponíveis
            </p>
            <div className="flex flex-wrap gap-2">
              {subjects
                .filter(s => s.subject_weight === 0)
                .map(s => {
                  const tinyFreeLocked = !!s.locked
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        if (tinyFreeLocked) {
                          if (subjectGate?.can_swap_now) setPickerOpen(true)
                          else if (subjectGate?.next_swap_at) {
                            const next = new Date(subjectGate.next_swap_at).toLocaleDateString('pt-MZ')
                            setLockedDetail(`Próxima troca em ${next}.`)
                            setLockedSubject(s)
                          } else showToast(gateLockedToast, 'warning')
                          return
                        }
                        if (s.requires_subscription && !isSubscribed) { setLockedDetail(null); setLockedSubject(s); return }
                        if (studyMode === 'exam' && examConfigMap[s.code])
                          navigate(`/exam?subject=${s.code}${navParams()}`)
                        else
                          continueToPractice(`/questions?subject=${s.code}${navParams()}`)
                      }}
                      className={`text-sm bg-white border px-4 py-3 rounded-xl transition-colors ${
                        tinyFreeLocked
                          ? 'text-gray-400 border-gray-200 cursor-not-allowed opacity-70'
                          : 'text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      {tinyFreeLocked ? '🔒 ' : (s.requires_subscription && !isSubscribed ? '🔒 ' : '')}
                      {EMOJI[s.code] ?? '📚'} {s.name}
                    </button>
                  )
                })}
            </div>
          </div>
        )}

        {/* Slice 1 (2026-05-12): keep the above-the-fold experience
            calmer. The subject grid is the main destination; softer
            coaching and account nudges live below it so the student
            can choose first, then browse guidance. */}

        {/* Exam-nudge banner (Companion-identity gamification PR,
            2026-05-03). Practice-only: under Mini/Exame the student is
            already choosing an assessment flow, so study nudges become
            noise rather than help. */}
        {showPracticeSupport && miniExamNudgeEligible && examNudgeMessage && (
          <MotivationalBanner message={examNudgeMessage} />
        )}

        {/* V2 delayed banners. Keep WhatsApp / grade-12 support here,
            but never re-show the course-discovery banner below the grid
            now that admission owns it in the top stack. */}
        {showPracticeSupport && user?.onboarding_style_v2 && (
          <SubjectsBanners
            student={user}
            onChanged={reloadAll}
            showReevaluateBanner={false}
          />
        )}

        {/* Placement nudge banner */}
        {showPracticeSupport && hasChosen && placementPending && firstPlacementCode && !localStorage.getItem('placement_banner_dismissed') && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-amber-900">Teste de nível pendente</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Faz o teste para personalizarmos as questões ao teu nível
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate(`/placement?subject=${firstPlacementCode}`)}
                className="text-xs bg-amber-500 text-white px-3 py-2 rounded-lg font-semibold hover:bg-amber-600 transition-colors"
              >
                Começar
              </button>
              <button
                onClick={() => { localStorage.setItem('placement_banner_dismissed', '1'); setPlacementPending(false) }}
                className="text-xs text-amber-400 hover:text-amber-600"
                title="Fechar"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Offline pack card - hidden from /subjects after download or manual
            dismiss. Both paths set a 2-day hide window - after that the card
            re-surfaces as a gentle nudge (pack may be stale by then). Student
            can always trigger / manage downloads from /profile.
            2026-04-28: also suppressed when the feature is locked (free tier
            or pack_questions_limit≤0) - the locked-state nudge belongs on
            /profile (where the student reviews plans) rather than the home
            grid. The unlock CTA still shows in ProfilePage. Demoted below the
            subject grid in Slice 1 because it's infrastructure, not the first
            learning decision. */}
        {showPracticeSupport && hasChosen && features
          && features.offline_packs
          && features.pack_questions_limit > 0
          && (() => {
          const dismissedUntil = Number(localStorage.getItem('offline_pack_dismissed_until') ?? '0')
          if (dismissedUntil > Date.now()) return null
          const hideForTwoDays = () => {
            localStorage.setItem(
              'offline_pack_dismissed_until',
              String(Date.now() + 2 * 86_400_000),
            )
            setSubjectsReloadKey(k => k + 1)
          }
          return (
            <OfflinePackCard
              offlinePacksEnabled={features.offline_packs}
              packQuestionsLimit={features.pack_questions_limit}
              dailyCreditsLimit={features.questions_per_day}
              subjectCodes={visibleSubjects.map(s => s.code)}
              onDismiss={hideForTwoDays}
              onDownloaded={hideForTwoDays}
            />
          )
        })()}

        {/* Legacy "no programme" CTA - only for pre-v2 students. V2 students
            rely on the ActiveContextSwitcher above; a fresh v2 student who's
            on the placeholder still has `hasChosen=false` but shouldn't see
            this card (the switcher's "Adicionar outro curso" is the right
            affordance, and the reevaluate banner handles course re-decision). */}
        {showPracticeSupport && !user?.onboarding_style_v2 && !hasChosen && (
          <div className="mt-6 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-indigo-800">Personaliza o teu estudo</p>
              <p className="text-xs text-indigo-600 mt-0.5">Escolhe o teu curso e organizamos as matérias por prioridade</p>
            </div>
            <button
              onClick={() => navigate('/onboarding')}
              className="shrink-0 text-xs bg-indigo-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Escolher curso
            </button>
          </div>
        )}

      </div>

      {/* ── Free-tier subject picker (2026-04-28) ──
          Auto-opens on first /subjects load when subject_gate.needs_initial_pick.
          Re-opens when the student taps a free-locked tile + cooldown elapsed,
          or via the "Trocar disciplinas" button on /profile. */}
      {pickerOpen && subjectGate && subjectGate.limit > 0 && (
        <FreeSubjectPickerModal
          subjects={subjects.map(s => ({ id: s.id, code: s.code, name: s.name, description: s.description }))}
          limit={subjectGate.effective_limit ?? subjectGate.limit}
          initialCodes={subjectGate.picked_codes}
          mode={subjectGate.needs_initial_pick ? 'first-pick' : 'swap'}
          canSwapNow={subjectGate.can_swap_now}
          nextSwapAt={subjectGate.next_swap_at}
          title={subjectGate.kind === 'placeholder_explore'
            ? `Escolhe até ${subjectGate.limit} disciplinas para começar`
            : undefined}
          description={subjectGate.kind === 'placeholder_explore'
            ? 'Enquanto ainda não escolheste um curso, podes praticar até 2 disciplinas. Escolhe as que mais precisas agora.'
            : undefined}
          savePath={subjectGate.kind === 'placeholder_explore'
            ? '/students/me/placeholder-subjects'
            : undefined}
          onClose={() => setPickerOpen(false)}
          onSaved={() => {
            setPickerOpen(false)
            // Invalidate the cache BEFORE bumping the reload key. The
            // student's picks (or curriculum) just changed → locked
            // state on subject cards is now stale. Without this,
            // setSubjectsReloadKey re-runs the effect, which reads
            // the stale cache first → renders the OLD locked state →
            // revalidation overwrites a moment later (visible flicker).
            invalidateSubjectsCache(user?.id)
            setSubjectsReloadKey(k => k + 1)
            void refreshAuthIfStale(true)
          }}
        />
      )}

      {/* Cooldown-elapsed prompt — auto-fires once per cooldown cycle */}
      {cooldownPromptOpen && subjectGate && (
        <CooldownElapsedPrompt
          pickedCodes={subjectGate.picked_codes}
          subjectNameByCode={Object.fromEntries(subjects.map(s => [s.code, s.name]))}
          cooldownDays={subjectGate.cooldown_days}
          onKeep={dismissCooldownPrompt}
          onSwap={() => {
            dismissCooldownPrompt()
            setPickerOpen(true)
          }}
        />
      )}

      {/* ── Upgrade prompt modal - shown when student taps a locked subject ── */}
      {lockedSubject && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => { setLockedSubject(null); setLockedDetail(null) }}
        >
          {/* Card surface matches the fullLockedSubject modal below, which is
              the house pattern for this page. */}
          <div
            className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6"
            onClick={e => e.stopPropagation()}
          >
            {/* subjects_limit, not the generic subscription_required: the
                student tapped a 12th-grade subject their plan does not cover,
                and naming that is the difference between "something is locked"
                and "here is what you would get". */}
            <UpgradePrompt
              reason="subjects_limit"
              compact
              detail={lockedDetail}
              onDismiss={() => { setLockedSubject(null); setLockedDetail(null) }}
            />
          </div>
        </div>
      )}

      {/* Full-exam lock-out modal - shown when student hits lifetime (free)
          or monthly (estudante) cap. Copy differs: "locked_forever" emphasises
          ranking stability + Premium re-attempt; monthly cap shows reset date. */}
      {fullLockedSubject && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setFullLockedSubject(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center"
            onClick={e => e.stopPropagation()}
          >
            {quota?.full.locked_forever ? (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Já fizeste o teu Exame Completo</h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">
                  A tua pontuação está no <strong>Ranking Oficial</strong>. Activa um dos planos pagos para fazeres mais Exames Completos!
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 text-left">
                  <p className="text-xs text-amber-800">
                    <strong>Entretanto:</strong> continua a treinar com Prática e Mini Exame - ambos actualizam a tua mestria e preparam-te para a vida real.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Limite mensal atingido</h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-2">
                  Este mês já fizeste os {quota?.full.month_limit ?? 0} Exames Completos do teu plano.
                  {quota?.full.month_resets_at && (
                    <> O limite renova a {new Date(quota.full.month_resets_at).toLocaleDateString('pt-MZ', { day: 'numeric', month: 'long' })}.</>
                  )}
                </p>
                {PAID_TIERS_ENABLED && nextTierName && (
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">
                    Activa o plano {nextTierName} para fazeres Exames Completos sem limite!
                  </p>
                )}
              </>
            )}
            {/* Both variants sell now. The monthly one is an upsell to an
                existing subscriber, which the founder asked for explicitly:
                come back next month, or move up a tier. It only appears once
                nextTierName resolves, so a failed lookup degrades to an
                explanation rather than a CTA that names nothing. */}
            {PAID_TIERS_ENABLED && (quota?.full.locked_forever || nextTierName) ? (
              <div className="flex flex-col items-stretch gap-2">
                <button
                  onClick={() => openPricing()}
                  className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700"
                >
                  Ver planos
                </button>
                <button
                  onClick={() => setFullLockedSubject(null)}
                  className="w-full py-2 rounded-xl text-gray-500 font-semibold text-sm hover:bg-gray-50"
                >
                  Entendido
                </button>
              </div>
            ) : (
              <button
                onClick={() => setFullLockedSubject(null)}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700"
              >
                Entendido
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Mini-exam weekly cap ──
          Same card pattern as the full-exam lock above. All three exam-lock
          modals on this page are now near-identical and would be better as one
          component keyed on the kind of lock; left for the cleanup pass rather
          than refactoring working code mid-feature.

          No new copy here: the title is the label the button already carried,
          and the reset sentence is the one the monthly-cap variant already
          uses. */}
      {miniLockedSubject && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setMiniLockedSubject(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Mini Exame desta semana usado</h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-2">
              Activa um dos planos pagos para fazeres mais Mini Exames!
            </p>
            {quota?.mini.next_available_at && (
              <p className="text-xs text-gray-400 leading-relaxed mb-4">
                O limite renova a {new Date(quota.mini.next_available_at).toLocaleDateString('pt-MZ', { day: 'numeric', month: 'long' })}.
              </p>
            )}
            {PAID_TIERS_ENABLED ? (
              <div className="flex flex-col items-stretch gap-2">
                <button
                  onClick={() => openPricing()}
                  className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700"
                >
                  Ver planos
                </button>
                <button
                  onClick={() => setMiniLockedSubject(null)}
                  className="w-full py-2 rounded-xl text-gray-500 font-semibold text-sm hover:bg-gray-50"
                >
                  Entendido
                </button>
              </div>
            ) : (
              <button
                onClick={() => setMiniLockedSubject(null)}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700"
              >
                Entendido
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mode selector modal removed - replaced by global toggle */}

      {/* ── Grade-12 popup gate (2026-04-29) ──
          Fires 15s after the student lands on /subjects (the grid),
          not earlier - gives them time to scan their courses without
          interruption. One-shot per student (localStorage flag).
          Timer survives parent re-renders via the useRef-based
          schedule inside Grade12PopupGate. */}
      <Grade12PopupGate
        user={user ? { id: user.id, is_current_grade_12: user.is_current_grade_12 } : null}
        onAnswered={async (chosen) => {
          // Refresh /auth/me so preferred_mode + is_current_grade_12 hydrate.
          await refreshAuthIfStale(true)
          // 'Sim' → student is in 12ª; the popup also stamped
          // preferred_mode='both' so the toggle is now visible. Flip
          // the local activeMode into the 12ª view immediately so the
          // student sees the change they just confirmed (otherwise
          // they'd answer Sim, the modal closes, and the page looks
          // identical - exact regression the user reported).
          if (chosen === 'yes') {
            setActiveMode('national_12')
          }
          // Keep the grade-12 context question ahead of the free-tier
          // 12ª subject picker. Once the student answers (or dismisses)
          // the popup, the mandatory picker may open if still needed.
          if (chosen === 'yes' && subjectGate?.needs_initial_pick) {
            setPickerOpen(true)
          }
        }}
      />

      {detailsProgId && (
        <ProgrammeDetailsSheet
          programmeId={detailsProgId}
          onClose={() => setDetailsProgId(null)}
        />
      )}

      {pendingPracticeHref && (
        <div
          className="fixed inset-0 z-50 bg-black/45 flex items-end sm:items-center justify-center p-4"
          onClick={resumePracticeAfterNudge}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-5 sm:p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0 mt-0.5" aria-hidden>✍️</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  Antes de começares
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mt-1.5">
                  Se tiveres papel ou caderno por perto, melhor ainda. Escrever com as tuas palavras ajuda-te a perceber e memorizar mais do que só ler.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={resumePracticeAfterNudge}
              className="mt-5 w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700"
            >
              Continuar para a prática
            </button>
          </div>
        </div>
      )}

      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}

      {/* Mini-simulação preset picker. Rendered once per page; opens when
          the student taps the Mini button on a subject card. On confirm we
          call /exam-sessions/start with type='mini' + the chosen preset, then
          navigate to the exam session page. Quota is re-fetched after return. */}
      {miniSubject && (
        <MiniPresetSheet
          open
          subjectName={miniSubject.name}
          onClose={() => setMiniSubject(null)}
          onConfirm={async (preset: MiniPreset) => {
            const subject = miniSubject
            setMiniSubject(null)
            // Hoisted out of the try so the catch block can use it for the
            // 404 "no questions for this university" message - was a TS2304
            // before this fix.
            const uniParam = isMultiGroup && selectedUniversity ? selectedUniversity : undefined
            try {
              const res = await api.post<{ session_id: string }>('/exam-sessions/start', {
                subject_code: subject.code,
                university: uniParam,
                type: 'mini',
                preset,
                // Backend's startSchema accepts mode='admission'|'national_12'
                // - pin it from the active toggle so a 'both' student in
                // the 12ª view actually gets a 12ª mini-exam pool.
                mode: activeMode,
              })
              // Analytics - mini session start. One of the six launch-critical
              // events; drives adoption + preset-mix funnels in PostHog.
              trackAnalytics('mini_session_started', {
                subject_code: subject.code,
                preset,
                university:   uniParam ?? null,
              })
              // Refresh quota so the cooldown line updates if they return.
              refreshQuota()
              // ?type=mini is the session-type discriminator (was `?mode=`
              // before commit 4 freed `mode` up for admission/national_12).
              // Also carry the active examMode so a 'both' student lands
              // back on the right toggle when they finish.
              navigate(`/exam?subject=${subject.code}&session=${res.session_id}&type=mini&mode=${activeMode}`)
            } catch (err: any) {
              // Server may still 402 if quota changed between /session-quota/me
              // and this POST (e.g. parallel tab). Surface a toast instead of alert().
              const reason = err?.data?.reason
              if (reason === 'mini_weekly_cap') {
                showToast('Já usaste o teu Mini Exame desta semana.', 'warning')
                refreshQuota()
              } else if (err?.status === 404) {
                // 2026-05-01: split copy by mode. National 12ª students aren't
                // browsing "by university" - INEE is one source, no
                // institutions to switch between. Avoid the misleading
                // "tenta outra universidade" prompt for them.
                if (activeMode === 'national_12') {
                  showToast(`Ainda não temos exame de ${subject.name} disponível. Tenta outra disciplina.`, 'warning')
                } else {
                  const uniLabel = uniParam ?? 'esta universidade'
                  showToast(`Ainda não temos questões de ${subject.name} para ${uniLabel}. Tenta outra universidade ou matéria.`, 'warning')
                }
              } else {
                showToast('Falha ao iniciar Mini Exame. Tenta novamente.', 'error')
              }
            }
          }}
        />
      )}
    </div>
  )
}

export default SubjectSelectPage
